import { useEffect, useMemo, useRef, useState } from "react";
import type { Block, DrawingRef, NarrativeItem, ProjectBrief, SpecialProvision, StandardId } from "../contracts/types";
import { isDesignServices } from "../contracts/types";
import { assemble, STANDARDS } from "../contracts/registry";
import { assembleHandoverLetter, assembleNoticeToProceed, assembleProgrammeRequest, assembleCustomLetter, assembleCommsRegister, nextCommsRef } from "../contracts/letters";
import { COMMS_META, type CommsKind } from "../contracts/types";
import { amountToWords } from "../contracts/amountToWords";
import { parseDrawingList } from "../contracts/drawingParse";
import { DrawingAnalysisPanel } from "./components/DrawingAnalysisPanel";
import { exportPdf, renderPdfBlob } from "./print/printContract";
import { ReviewPanel } from "./components/ReviewPanel";
import { createReview, pushSnapshot } from "./reviewApi";
import { ShareButtons } from "./components/ShareButtons";
import FileAttach from "./components/FileAttach";
import { PaymentsApp } from "./components/PaymentsApp";
import { AssistantChat } from "./components/AssistantChat";
import type { ContractSeed, PaymentCert, PaymentProject, CertBuildup } from "../payments/types";
import { isAdvanceCert, nextCertNumber, previouslyCertifiedAuto, advancePaid as advancePaidOn, worksCertified } from "../payments/types";
import { loadPayments, nowDate, projectFromContract, uid } from "../payments/store";
import { applyToLedger, refreshLedgerCache } from "./ledgerWrite";
import type { PaperSize, ReviewComment, ReviewReply, ReviewStatus } from "../contracts/types";
import { assembleIpc, computeIpc, type IpcData, type IpcValuation, netCertified } from "../contracts/paymentCertificate";
import { SAMPLE_PROJECT, blankProject, blankArchitectureProject } from "./sampleProject";
import { generateDocx } from "./docx/generateDocx";
import { ContractPreview } from "./components/ContractPreview";
import { AiTextArea } from "./components/AiTextArea";
import { PdfImport } from "./components/PdfImport";
import { ConflictScanner } from "./components/ConflictScanner";
import { Landing, type LandingMode } from "./components/Landing";
import { ContractGate } from "./components/ContractGate";
import { AuthGate } from "./components/AuthGate";
import { getMe, logout, getSyncedContracts, putSyncedContracts } from "./authApi";
import { extractManyPdfs } from "./pdf/extractText";
import { deleteNamed, exportJson, importJson, listProjects, loadCurrent, loadNamed, saveCurrent, saveNamed, getNamedMap, mergeNamedMap } from "./projectStore";
import { readInboundAtelier, sendProjectToAtelier } from "./atelierBridge";
import { sendToStudio, listenForStudioLink, type StudioTarget, type StudioLinkRef } from "./studioBridge";

const numStr = (n?: number) => (n == null || Number.isNaN(n) ? "" : String(n));
const num = (v: string) => (v.trim() === "" ? NaN : Number(v));

// Find the index of an existing Special Conditions heading block that a
// `specialConditionsEdit.match` refers to — by its printed number ("2.4", "16",
// "Clause 12") or a distinctive word from its title. Returns -1 if not found.
function findScHeadingIdx(blocks: Block[], match: string): number {
  const m = (match || "").trim();
  if (!m) return -1;
  const numMatch = /^(?:clause\s+)?(\d+(?:\.\d+)*)/i.exec(m);
  const wantNum = numMatch ? numMatch[1] : null;
  const lc = m.toLowerCase();
  let fallback = -1;
  for (let i = 0; i < blocks.length; i++) {
    const bl = blocks[i];
    if (bl.type !== "heading") continue;
    const t = (bl.text || "").trim();
    const stripped = t.replace(/^clause\s+/i, "");
    if (wantNum) {
      const re = new RegExp("^" + wantNum.replace(/\./g, "\\.") + "(?![\\d.])");
      if (re.test(stripped)) return i;
    }
    if (lc && t.toLowerCase().includes(lc) && fallback < 0) fallback = i;
  }
  return fallback;
}

// Pure merge of an assistant/smart-fix patch into a brief. `specialConditionsEdit`
// rewrites/deletes existing Special Conditions clauses in place; `specialConditionsAdd`
// appends new numbered clauses; everything else shallow-merges onto the brief.
function mergeAssistPatch(b: ProjectBrief, p: any): ProjectBrief {
  const { specialConditionsAdd, specialConditionsEdit, ...rest } = p || {};
  let merged: ProjectBrief = { ...b, ...rest };
  let blocks: Block[] = [...(merged.specialConditionsBlocks || [])];

  // 1) EDITS — replace or delete existing clauses in place.
  const edits = Array.isArray(specialConditionsEdit)
    ? specialConditionsEdit.filter((x: any) => x && x.match && (x.action === "replace" || x.action === "delete"))
    : [];
  for (const ed of edits) {
    const idx = findScHeadingIdx(blocks, String(ed.match));
    if (idx < 0) continue;
    const startLevel = (blocks[idx] as any).level ?? 2;
    // The clause spans from its heading to just before the next heading of the
    // same or higher level (so sub-content / lists under it are included).
    let end = idx + 1;
    while (end < blocks.length && !(blocks[end].type === "heading" && (((blocks[end] as any).level ?? 2) <= startLevel))) end++;
    if (ed.action === "delete") {
      blocks.splice(idx, end - idx);
      continue;
    }
    // replace: keep the original leading numbering, swap title (if given) + body.
    const origHead = ((blocks[idx] as any).text || "").trim();
    const numTok = /^(Clause\s+\d+(?:\.\d+)*|\d+(?:\.\d+)*)/i.exec(origHead);
    const newTitle = (ed.heading || "").trim();
    let headText = origHead;
    if (newTitle) {
      const prefix = numTok ? numTok[1] : "";
      headText = prefix ? (/^clause/i.test(prefix) ? `${prefix} — ${newTitle}` : `${prefix}  ${newTitle}`) : newTitle;
    }
    const newHead: Block = { type: "heading", text: headText, level: startLevel };
    const body: Block[] = ed.text && String(ed.text).trim() ? [{ type: "paragraph", text: String(ed.text).trim() }] : [];
    blocks.splice(idx, end - idx, newHead, ...body);
  }

  // 2) ADDS — append new numbered clauses (before any "Final Provisions" close).
  const add = Array.isArray(specialConditionsAdd) ? specialConditionsAdd.filter((x: any) => x && (x.text || x.heading)) : [];
  if (add.length) {
    const nums = blocks
      .filter((bl): bl is Extract<Block, { type: "heading" }> => bl.type === "heading")
      .map((bl) => { const m = /^Clause\s+(\d+)/i.exec(bl.text || ""); return m ? parseInt(m[1], 10) : 0; });
    let next = (nums.length ? Math.max(...nums) : 0) + 1;
    const newBlocks: Block[] = [];
    for (const it of add) {
      const title = (it.heading || "").trim();
      newBlocks.push({ type: "heading", text: `Clause ${next} — ${title || "Special Condition"}`, level: 2 });
      newBlocks.push({ type: "paragraph", text: (it.text || "").trim() });
      next++;
    }
    const fpIdx = blocks.findIndex((bl) => bl.type === "heading" && /final provisions/i.test((bl as any).text || ""));
    blocks = fpIdx >= 0 ? [...blocks.slice(0, fpIdx), ...newBlocks, ...blocks.slice(fpIdx)] : [...blocks, ...newBlocks];
  }

  if (edits.length || add.length) merged = { ...merged, specialConditionsBlocks: blocks };
  return merged;
}

const DEFAULT_IPC: IpcData = {
  kind: "interim",
  certificateNo: "IPC 1",
  date: "",
  currency: "USD",
  statementRef: "",
  statementDate: "",
  periodFrom: "",
  periodTo: "",
  valuation: { worksExecuted: NaN, materialsOnSite: NaN, variations: NaN, provisionalSums: NaN, dayworks: NaN, priceAdjustment: NaN, otherAdditions: NaN },
  retentionPercent: 10,
  retentionLimitPercent: 10,
  retentionReleased: NaN,
  advanceRecoveredToDate: NaN,
  previouslyCertified: NaN,
  vatPercent: NaN,
  paymentDueDays: 42,
  advancePercent: 15,
  installmentIndex: 1,
  installmentCount: 3,
  advanceAmount: 700000,
  amountWords: "",
  engineerName: "",
};

export function App() {
  // A project handed over from Atelier (?atelier=…) opens directly as a draft;
  // otherwise restore the autosaved working draft, else the sample.
  const [brief, setBrief] = useState<ProjectBrief>(() =>
    readInboundAtelier(typeof window !== "undefined" ? window.location.search : "") || loadCurrent() || SAMPLE_PROJECT);
  // Deep links: ?go=contract|ipc|payments|review jumps straight to an interface
  // (pinnable per-interface URLs); ?ledger= is a client opening their ledger portal.
  const search = typeof window !== "undefined" ? window.location.search : "";
  const goParam = new URLSearchParams(search).get("go");
  const resetToken = new URLSearchParams(search).get("reset");
  // Client tracking links (?ledger=) bypass the owner login — they're read-only views.
  const deepLink = /[?&]ledger=/.test(search);
  const inboundAtelier = /[?&]atelier=/.test(search);
  // Owner session: undefined = checking, null = signed out, string = email.
  const [user, setUser] = useState<string | null | undefined>(undefined);
  useEffect(() => { getMe().then((m) => setUser(m.email)); }, []);
  async function doSignOut() { try { await logout(); } catch {} setUser(null); }
  // When signed in, pull the account's synced contracts (merge) and push local up
  // so every contract follows the login across devices.
  const syncedOnce = useRef(false);
  useEffect(() => {
    if (!user || syncedOnce.current) return;
    syncedOnce.current = true;
    (async () => {
      try {
        const r = await getSyncedContracts();
        const remote = r.data && Object.keys(r.data).length ? r.data : null;
        if (remote) { mergeNamedMap(remote); setSaved(listProjects()); }
        const local = getNamedMap();
        // Only push when we actually have projects — never overwrite the account
        // with an empty map (that's what silently wiped it before).
        if (Object.keys(local).length) await putSyncedContracts(local);
      } catch {}
    })();
  }, [user]);
  const pushContracts = () => { if (user) putSyncedContracts(getNamedMap()).catch(() => {}); };
  const initialMode = goParam === "ipc" ? "ipc" : goParam === "payments" || deepLink ? "payments" : goParam === "review" ? "review" : goParam === "scan" ? "scan" : "contract";
  const [mode, setMode] = useState<"contract" | "ipc" | "review" | "payments" | "scan">(initialMode as any);
  // Landing page on entry; a deep link (?go= / ?ledger=) skips straight in.
  const [entered, setEntered] = useState(() => {
    if (deepLink || goParam || inboundAtelier) return true;
    try { return sessionStorage.getItem("cg:entered") === "1"; } catch { return false; }
  });
  // Contract Studio opens a project gate (open saved / continue / new) before the editor.
  const [contractGate, setContractGate] = useState(goParam === "contract");
  const enter = (m?: LandingMode) => {
    if (m) setMode(m);
    if (m === "contract") setContractGate(true);
    try { sessionStorage.setItem("cg:entered", "1"); } catch {}
    setEntered(true);
  };
  const goHome = () => { try { sessionStorage.removeItem("cg:entered"); } catch {} setContractGate(false); setEntered(false); };
  const [payAutoLink, setPayAutoLink] = useState(false);
  const [ledgerMsg, setLedgerMsg] = useState<string | null>(null);
  const [ledgerBusy, setLedgerBusy] = useState(false);
  // The ledger as the IPC tab sees it (cache, refreshed from the cloud on entry)
  // and the contract this project's certificates are recorded under.
  const [ledgerProjects, setLedgerProjects] = useState<PaymentProject[]>(() => loadPayments().projects);
  const [ipcTarget, setIpcTarget] = useState<{ projectId: string; contractId: string } | null>(null);
  // Hand-off to the Payments screen: which project/contract/certificate to open on.
  const [payFocus, setPayFocus] = useState<{ projectId: string; contractId?: string; certId?: string } | null>(null);
  const [aiReady, setAiReady] = useState<boolean | null>(null);

  useEffect(() => {
    fetch("/api/health")
      .then((r) => (r.ok ? r.json() : { ai: false }))
      .then((d: any) => setAiReady(!!d.ai))
      .catch(() => setAiReady(false));
  }, []);
  // The certificate currency follows the contract's, not a hard-coded USD.
  const [ipc, setIpc] = useState<IpcData>(() => ({ ...DEFAULT_IPC, currency: brief.contractPrice.currency || DEFAULT_IPC.currency }));
  const ipcTouched = useRef(false);
  useEffect(() => {
    // A different project loaded and the certificate hasn't been edited yet:
    // follow its currency.
    if (!ipcTouched.current && brief.contractPrice.currency) setIpc((s) => (s.currency === brief.contractPrice.currency ? s : { ...s, currency: brief.contractPrice.currency }));
  }, [brief.contractPrice.currency]);
  const [aiGoal, setAiGoal] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [genBusy, setGenBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const [paperSize, setPaperSize] = useState<PaperSize>("A4");
  const [saved, setSaved] = useState(() => listProjects());
  const [savedTick, setSavedTick] = useState<string | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [showAnalysis, setShowAnalysis] = useState(false);

  // Autosave the working draft (debounced) so a reload never loses work.
  useEffect(() => {
    const id = setTimeout(() => saveCurrent(brief), 400);
    return () => clearTimeout(id);
  }, [brief]);

  // A project handed over from Atelier: strip the payload from the URL and flag it.
  useEffect(() => {
    if (!inboundAtelier) return;
    try { window.history.replaceState({}, "", window.location.pathname); } catch {}
    flash("Imported from Atelier — review & save");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function flash(msg: string) {
    setSavedTick(msg);
    setTimeout(() => setSavedTick((m) => (m === msg ? null : m)), 2200);
  }
  function doSaveAs() {
    const name = window.prompt("Save project as:", brief.projectName || "Untitled project");
    if (!name) return;
    saveNamed(name, brief);
    setSaved(listProjects());
    pushContracts();
    flash(`Saved “${name}”${user ? " · synced to your account" : ""}`);
  }
  // Quick save: save under the current project name without prompting (falls back
  // to "Save as…" when the contract has no name yet). Syncs to the account.
  function doSave() {
    const name = (brief.projectName || "").trim();
    if (!name) { doSaveAs(); return; }
    saveNamed(name, brief);
    setSaved(listProjects());
    pushContracts();
    flash(`Saved “${name}”${user ? " · synced to your account" : ""}`);
  }
  function doLoadNamed(name: string) {
    const b = loadNamed(name);
    if (b) { setBrief(b); flash(`Loaded “${name}”`); }
  }
  function doDeleteNamed(name: string) {
    if (!window.confirm(`Delete saved project “${name}”?`)) return;
    deleteNamed(name);
    setSaved(listProjects());
    pushContracts();
  }
  async function doImport(file: File | null | undefined) {
    if (!file) return;
    try {
      const b = await importJson(file);
      setBrief(b);
      flash(`Imported ${file.name}`);
    } catch (e: any) {
      flash(`Import failed: ${String(e.message).slice(0, 60)}`);
    }
    if (importRef.current) importRef.current.value = "";
  }

  const contract = useMemo(() => (mode === "ipc" ? assembleIpc(brief, ipc) : assemble(brief)), [brief, ipc, mode]);
  const reviewAnchors = useMemo(() => [{ value: "__whole__", label: "General / whole contract" }, ...assemble(brief).parts.map((p) => ({ value: p.id, label: p.name }))], [brief]);

  const patch = (p: Partial<ProjectBrief>) => setBrief((b) => ({ ...b, ...p }));
  const patchParty = (k: "employer" | "contractor" | "engineer", p: Record<string, string>) =>
    setBrief((b) => ({ ...b, [k]: { ...(b[k] || { name: "", address: "" }), ...p } }));
  // Design-services (Owner–Architect) standards relabel parties and swap the
  // commercial fields (fee/services) for the construction ones.
  const design = isDesignServices(brief.standard);
  const patchIpc = (p: Partial<IpcData>) => { ipcTouched.current = true; setIpc((s) => ({ ...s, ...p })); };

  // Scan a contractor's payment application / statement / invoice (PDF) and
  // auto-fill the FIDIC IPC valuation build-up.
  const [ipcScanBusy, setIpcScanBusy] = useState(false);
  const [ipcScanMsg, setIpcScanMsg] = useState<string | null>(null);
  const ipcScanRef = useRef<HTMLInputElement>(null);
  async function scanIpcInvoice(file?: File | null) {
    if (!file) return;
    setIpcScanBusy(true);
    setIpcScanMsg("Reading document…");
    try {
      const results = await extractManyPdfs([file]);
      const text = results.map((r) => r.text).join("\n");
      if (!text.trim()) throw new Error("No selectable text found (scanned image?).");
      setIpcScanMsg("Extracting the payment build-up…");
      const res = await fetch("/api/extract-ipc", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }) });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const { ipc: x } = (await res.json()) as { ipc?: Record<string, any> };
      if (!x) throw new Error("No fields returned.");
      const numOr = (v: any) => (v != null && isFinite(Number(v)) ? Number(v) : undefined);
      const dk = String(x.documentKind || "interim");

      if (dk === "supply") {
        setIpcScanMsg("This looks like a supplier invoice — use Payments mode → “⤒ Scan quotation” to raise a supply payment certificate.");
        setTimeout(() => setIpcScanMsg(null), 7000);
        return;
      }

      // Common fields
      const patch: Partial<IpcData> = {};
      if (x.statementRef) patch.statementRef = String(x.statementRef);
      if (x.statementDate) patch.statementDate = String(x.statementDate);
      if (x.currency) patch.currency = String(x.currency);

      if (dk === "advance" || dk === "release") {
        // Advance / mobilisation / further-release payment → advance certificate.
        patch.kind = "advance";
        const amt = numOr(x.amountThisCertificate);
        if (amt != null) patch.advanceAmount = amt;
        if (numOr(x.advancePercent) != null) patch.advancePercent = numOr(x.advancePercent);
        if (x.guaranteeRef) patch.guaranteeRef = String(x.guaranteeRef);
        if (numOr(x.installmentIndex) != null) patch.installmentIndex = numOr(x.installmentIndex);
        if (numOr(x.installmentCount) != null) patch.installmentCount = numOr(x.installmentCount);
        if (x.description) patch.notes = String(x.description);
        setIpc((s) => ({ ...s, ...patch }));
        setIpcScanMsg(`Prepared an ${dk === "release" ? "installment / release" : "advance"} certificate${amt != null ? ` for ${patch.currency || ipc.currency} ${amt.toLocaleString()}` : ""} — review below.`);
      } else {
        // Interim works valuation build-up.
        patch.kind = "interim";
        if (x.periodFrom) patch.periodFrom = String(x.periodFrom);
        if (x.periodTo) patch.periodTo = String(x.periodTo);
        if (numOr(x.retentionPercent) != null) patch.retentionPercent = numOr(x.retentionPercent);
        if (numOr(x.previouslyCertified) != null) patch.previouslyCertified = numOr(x.previouslyCertified);
        if (numOr(x.vatPercent) != null) patch.vatPercent = numOr(x.vatPercent);
        const val: Partial<IpcValuation> = {};
        for (const k of ["worksExecuted", "materialsOnSite", "variations", "provisionalSums", "dayworks", "priceAdjustment", "otherAdditions"] as const) {
          const n = numOr(x[k]); if (n != null) (val as any)[k] = n;
        }
        if (Object.keys(val).length) patch.valuation = { ...(ipc.valuation || {}), ...val };
        setIpc((s) => ({ ...s, ...patch }));
        setIpcScanMsg(`Filled the interim valuation from the document — review the build-up below.`);
      }
      setTimeout(() => setIpcScanMsg(null), 6000);
    } catch (e: any) {
      setIpcScanMsg(`Scan failed: ${String(e?.message || e).slice(0, 100)}`);
      setTimeout(() => setIpcScanMsg(null), 6000);
    } finally {
      setIpcScanBusy(false);
      if (ipcScanRef.current) ipcScanRef.current.value = "";
    }
  }

  // Drawings
  const addDrawing = () => setBrief((b) => ({ ...b, drawings: [...b.drawings, { number: "", title: "", revision: "", discipline: "" }] }));
  const updDrawing = (i: number, p: Partial<DrawingRef>) => setBrief((b) => ({ ...b, drawings: b.drawings.map((d, j) => (j === i ? { ...d, ...p } : d)) }));
  const rmDrawing = (i: number) => setBrief((b) => ({ ...b, drawings: b.drawings.filter((_, j) => j !== i) }));

  // Narrative
  const addNarr = () => setBrief((b) => ({ ...b, narrative: [...b.narrative, { marker: "", heading: "", text: "" }] }));
  const updNarr = (i: number, p: Partial<NarrativeItem>) => setBrief((b) => ({ ...b, narrative: b.narrative.map((n, j) => (j === i ? { ...n, ...p } : n)) }));
  const rmNarr = (i: number) => setBrief((b) => ({ ...b, narrative: b.narrative.filter((_, j) => j !== i) }));

  // Special provisions
  const addSP = () => setBrief((b) => ({ ...b, specialProvisions: [...b.specialProvisions, { heading: "", text: "", clauseRef: "", kind: "add" }] }));
  const updSP = (i: number, p: Partial<SpecialProvision>) => setBrief((b) => ({ ...b, specialProvisions: b.specialProvisions.map((s, j) => (j === i ? { ...s, ...p } : s)) }));
  const rmSP = (i: number) => setBrief((b) => ({ ...b, specialProvisions: b.specialProvisions.filter((_, j) => j !== i) }));

  // Advance disbursement schedule
  const advSched = () => brief.advanceSchedule || [];
  const addAdvInst = () => patch({ advanceSchedule: [...advSched(), { label: `Installment ${advSched().length + 1}`, amount: { amount: NaN, currency: brief.contractPrice.currency }, trigger: "" }] });
  const updAdvInst = (i: number, p: Partial<NonNullable<ProjectBrief["advanceSchedule"]>[number]>) => patch({ advanceSchedule: advSched().map((x, j) => (j === i ? { ...x, ...p } : x)) });
  const rmAdvInst = (i: number) => patch({ advanceSchedule: advSched().filter((_, j) => j !== i) });
  const moveAdvInst = (i: number, dir: number) => { const arr = [...advSched()]; const j = i + dir; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; patch({ advanceSchedule: arr }); };
  // Detect stored advance-payment prose clauses that conflict with the schedule.
  // These can live in THREE places: specialProvisions, narrative items, and the
  // standalone specialConditionsBlocks — the offending "1. Advance Payment…three
  // installments" text is usually a narrative item, so we sweep all three.
  // A dedicated advance heading OR advance-clause text (needs a clause-specific
  // marker so a passing "…advance payment and its repayment…" mention in a
  // general "Project Terms" item is NOT wrongly removed).
  const advHeadRe = /\badvance\b/i;
  const advMarkerRe = /(instal?lment|disburs|Appendix to Tender|fixed sum|mobilis|mobiliz|three \(3\))/i;
  const isAdvanceClauseText = (t: string) => /advance payment/i.test(t) && advMarkerRe.test(t);
  const isAdvanceProvision = (p: SpecialProvision) => p.clauseRef === "14.2" || advHeadRe.test(p.heading || "") || isAdvanceClauseText(p.text || "");
  const isAdvanceNarrative = (n: NarrativeItem) => advHeadRe.test(n.heading || "") || isAdvanceClauseText(n.text || "");
  const isAdvanceBlock = (bl: Block) => isAdvanceClauseText((bl as any).text || "");
  const advProvCount =
    brief.specialProvisions.filter(isAdvanceProvision).length +
    brief.narrative.filter(isAdvanceNarrative).length +
    (brief.specialConditionsBlocks || []).filter(isAdvanceBlock).length;
  const clearAdvanceProvisions = () => patch({
    specialProvisions: brief.specialProvisions.filter((p) => !isAdvanceProvision(p)),
    narrative: brief.narrative.filter((n) => !isAdvanceNarrative(n)),
    specialConditionsBlocks: (brief.specialConditionsBlocks || []).filter((bl) => !isAdvanceBlock(bl)),
  });

  // Apply an assistant patch. Two convenience fields drive the standalone Special
  // Conditions of Contract (Section 5):
  //   specialConditionsEdit → REWRITE/DELETE an existing clause IN PLACE (by number
  //     or heading keyword), so a correction rewrites the affected clause instead of
  //     stacking a new "Supersedes Clause X" clause.
  //   specialConditionsAdd  → APPEND new numbered "Clause N" clauses (new subjects).
  const applyAssistPatch = (p: any) => setBrief((b) => mergeAssistPatch(b, p));

  // Smart-fix: apply a patch produced from the conflict scan, then persist it under
  // the current project name and sync to the account so the fix isn't lost.
  const applyFixes = (p: any) => {
    const nb = mergeAssistPatch(brief, p);
    setBrief(nb);
    const name = (nb.projectName || "").trim();
    if (name) { saveNamed(name, nb); setSaved(listProjects()); pushContracts(); }
  };

  // IPC lines
  const patchVal = (p: Partial<IpcValuation>) => patchIpc({ valuation: { ...(ipc.valuation || {}), ...p } });

  // Review (local) handlers
  const reviewList = brief.review || [];
  const reviewAdd = (c: ReviewComment) => patch({ review: [c, ...reviewList] });
  const reviewReply = (id: string, r: ReviewReply) => patch({ review: reviewList.map((x) => (x.id === id ? { ...x, replies: [...x.replies, r] } : x)) });
  const reviewStatus = (id: string, s: ReviewStatus) => patch({ review: reviewList.map((x) => (x.id === id ? { ...x, status: s } : x)) });
  const reviewRemove = (id: string) => patch({ review: reviewList.filter((x) => x.id !== id) });

  const [shareBusy, setShareBusy] = useState(false);
  const [shareErr, setShareErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  /** Which drawer is open above the sticky action bar. Only one at a time — the
   * bar sits over the form, so anything left open steals the form's space. */
  const [panel, setPanel] = useState<"share" | "letters" | "executed" | "ledger" | null>(null);
  const reviewUrl = brief.reviewId ? `${window.location.origin}/?review=${brief.reviewId}` : "";

  // Share the contract: create a live link on first use (or refresh the shared copy
  // with the current document), then reveal the share panel with copy/WhatsApp/email.
  async function doShareContract() {
    setPanel("share");
    if (!brief.reviewId) await createShareLink();
    else await pushDocUpdate();
  }

  async function createShareLink() {
    setShareBusy(true);
    setShareErr(null);
    try {
      const { id } = await createReview({ contract, comments: reviewList, title: brief.projectName || "Contract", ref: brief.contractReference, createdAt: new Date().toISOString() });
      patch({ reviewId: id });
    } catch (e: any) {
      setShareErr(`Could not create shared review (${String(e.message).slice(0, 80)}).`);
    } finally {
      setShareBusy(false);
    }
  }
  async function pushDocUpdate() {
    if (!brief.reviewId) return;
    setShareBusy(true);
    setShareErr(null);
    try {
      await pushSnapshot(brief.reviewId, contract, brief.projectName || "Contract");
    } catch (e: any) {
      setShareErr(`Update failed (${String(e.message).slice(0, 80)}).`);
    } finally {
      setShareBusy(false);
    }
  }
  function copyLink() {
    navigator.clipboard?.writeText(reviewUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }

  // Share a payment certificate (IPC mode) via its own live link, kept separate
  // from the contract's link so sharing a certificate never overwrites it.
  const [certReviewId, setCertReviewId] = useState<string | null>(null);
  const [showCertShare, setShowCertShare] = useState(false);
  const [certCopied, setCertCopied] = useState(false);
  const certReviewUrl = certReviewId ? `${window.location.origin}/?review=${certReviewId}` : "";
  const certTitle = () => `${brief.projectName || "Contract"} — ${ipc.certificateNo || "Payment Certificate"}`;
  async function doShareCert() {
    setShowCertShare(true);
    setShareErr(null);
    setShareBusy(true);
    try {
      if (!certReviewId) {
        const { id } = await createReview({ contract, comments: [], title: certTitle(), ref: ipc.certificateNo, createdAt: new Date().toISOString() });
        setCertReviewId(id);
      } else {
        await pushSnapshot(certReviewId, contract, certTitle());
      }
    } catch (e: any) {
      setShareErr(`Could not create share link (${String(e.message).slice(0, 80)}).`);
    } finally {
      setShareBusy(false);
    }
  }
  function copyCertLink() {
    navigator.clipboard?.writeText(certReviewUrl).then(() => { setCertCopied(true); setTimeout(() => setCertCopied(false), 1500); });
  }

  // Contract-administration letters generated from the brief (Site Handover /
  // Notice to Proceed), exported through the same Word/PDF pipeline.
  // When the Project Studio creates a job from this contract it posts back — keep
  // the link so we can jump straight to the tender/project we opened.
  const [studioLink, setStudioLink] = useState<(StudioLinkRef & { target: StudioTarget }) | null>(null);
  useEffect(() => listenForStudioLink((target, ref) => setStudioLink({ ...ref, target })), []);

  /** The standard letters, in the order they arise on a job. Drives both the
   * action-bar drawer and the "Letters & notices" group. */
  const LETTERS: { kind: CommsKind; name: string; clause: string }[] = [
    { kind: "handover", name: "Site Handover Letter", clause: "Sub-Clause 2.1" },
    { kind: "ntp", name: "Notice to Proceed", clause: "Sub-Clause 8.1" },
    { kind: "programme", name: "Request for Programme", clause: "Sub-Clause 8.3" },
  ];

  const [letterBusy, setLetterBusy] = useState<string | null>(null);
  // Free-form letter to the Contractor (transient — not saved into the contract).
  const [letterSubject, setLetterSubject] = useState("");
  const [letterBody, setLetterBody] = useState("");
  const [letterFrom, setLetterFrom] = useState<"engineer" | "employer">("engineer");
  // The subject each communication is logged under (must match what the letter prints).
  const SUBJECTS: Record<CommsKind, string> = {
    handover: "Handover of the Site — Right of Access and Possession",
    ntp: "Notice to Proceed — Commencement Date",
    programme: "Request for the Contractor's Detailed Time Programme",
    letter: "",
  };
  const subjectFor = (kind: CommsKind) => (kind === "letter" ? letterSubject.trim() || "Letter to the Contractor" : SUBJECTS[kind]);
  const commsDate = () => brief.letterDate || brief.coverDate || "—";
  /** Reuse the reference already logged for this same letter/date, else take the
   * next in sequence — so exporting Word then PDF doesn't burn two references. */
  const refFor = (kind: CommsKind) => {
    const existing = (brief.commsLog || []).find((e) => e.kind === kind && e.subject === subjectFor(kind) && e.date === commsDate());
    return existing ? existing.ref : nextCommsRef(brief.commsLog, kind);
  };
  const issuerName = (kind: CommsKind) => {
    // The Consultant issues the contract-administration letters (the handover is
    // conveyed on the Employer's behalf); only the free-form letter can be sent
    // expressly by the Employer.
    if (kind === "letter" && letterFrom === "employer") return brief.employer?.name || "Employer";
    return brief.engineer?.name?.trim() ? brief.engineer.name : brief.employer?.name || "Employer";
  };
  /** Record the communication in the register (merging formats for the same letter). */
  function logComms(kind: CommsKind, ref: string, fmt: "docx" | "pdf" | "share") {
    const subject = subjectFor(kind);
    const date = commsDate();
    const label = fmt === "docx" ? "Word" : fmt === "pdf" ? "PDF" : "Link";
    setBrief((b) => {
      const log = [...(b.commsLog || [])];
      const i = log.findIndex((e) => e.kind === kind && e.subject === subject && e.date === date);
      if (i >= 0) {
        const formats = new Set((log[i].format || "").split(", ").filter(Boolean));
        formats.add(label);
        log[i] = { ...log[i], format: [...formats].join(", ") };
      } else {
        log.push({ id: `${kind}-${ref}-${log.length}`, kind, ref, subject, date, issuedBy: issuerName(kind), issuedTo: b.contractor?.name || "the Contractor", format: label });
      }
      return { ...b, commsLog: log };
    });
  }

  // Our digital signature for letters — the same approval signature managed in the
  // Payments ledger. Loaded once; letters we issue carry it so a shared letter is
  // signed by us, ready for the Contractor to review (they don't countersign).
  const [approvalSig, setApprovalSig] = useState<string | undefined>(() => loadPayments().signature);
  const [signLetters, setSignLetters] = useState(true);
  // The signature is managed in Payments; pick up a newly-set one when the drawer opens.
  useEffect(() => { if (panel === "letters") setApprovalSig(loadPayments().signature); }, [panel]);

  /** Build the letter document for a kind — shared by export and share, so a
   * shared link and a downloaded file are always the same document. */
  const buildLetter = (kind: CommsKind, ref: string) => {
    const sign = signLetters && approvalSig ? { signature: approvalSig, signedDate: brief.letterDate || brief.coverDate } : undefined;
    return kind === "handover" ? assembleHandoverLetter(brief, ref, sign)
      : kind === "ntp" ? assembleNoticeToProceed(brief, ref, sign)
      : kind === "programme" ? assembleProgrammeRequest(brief, ref, sign)
      : assembleCustomLetter(brief, letterSubject, letterBody, { from: letterFrom, reference: ref, sign });
  };

  async function genLetter(kind: CommsKind, fmt: "docx" | "pdf") {
    setLetterBusy(kind + fmt);
    setPdfErr(null);
    try {
      const ref = refFor(kind);
      const doc = buildLetter(kind, ref);
      if (fmt === "docx") await generateDocx(doc, paperSize);
      else await exportPdf(doc, paperSize);
      logComms(kind, ref, fmt);
    } catch (e: any) {
      setPdfErr(`Letter export failed: ${String(e?.message || e).slice(0, 120)}`);
    } finally {
      setLetterBusy(null);
    }
  }

  /** Share a letter as a live link the Contractor can open — the same route the
   * contract and certificate use, so it can go out over WhatsApp or email. The
   * link id is kept on the register entry, so re-sharing refreshes that link
   * rather than minting a second one for the same communication. */
  const [letterShare, setLetterShare] = useState<{ kind: CommsKind; id: string } | null>(null);
  const letterShareUrl = letterShare ? `${location.origin}/?review=${letterShare.id}` : "";
  const [letterCopied, setLetterCopied] = useState(false);
  async function doShareLetter(kind: CommsKind) {
    setLetterBusy(kind + "share");
    setPdfErr(null);
    try {
      const ref = refFor(kind);
      const doc = buildLetter(kind, ref);
      const subject = subjectFor(kind);
      const title = `${brief.projectName || "Project"} — ${subject}`;
      const existing = (brief.commsLog || []).find((e) => e.kind === kind && e.subject === subject && e.date === commsDate());
      let id = existing?.shareId;
      if (id) await pushSnapshot(id, doc, title);
      else id = (await createReview({ contract: doc, comments: [], title, ref, createdAt: new Date().toISOString() })).id;
      // Sharing is issuing — log it, then stamp the link onto that entry.
      logComms(kind, ref, "share");
      setBrief((b) => {
        const log = [...(b.commsLog || [])];
        const i = log.findIndex((e) => e.kind === kind && e.subject === subject && e.date === commsDate());
        if (i >= 0) log[i] = { ...log[i], shareId: id };
        return { ...b, commsLog: log };
      });
      setLetterShare({ kind, id: id! });
    } catch (e: any) {
      setPdfErr(`Could not create the letter link (${String(e?.message || e).slice(0, 80)}).`);
    } finally {
      setLetterBusy(null);
    }
  }
  function copyLetterLink() {
    navigator.clipboard?.writeText(letterShareUrl).then(() => { setLetterCopied(true); setTimeout(() => setLetterCopied(false), 1500); });
  }
  /** The letter's full printed reference, as it appears on the letterhead. */
  const letterRef = (kind: CommsKind) => `${brief.contractReference || ""}${brief.contractReference ? "/" : ""}${refFor(kind)}`;
  /** A transmittal, not an invitation to comment — this letter is being issued. */
  const letterTransmittal = (kind: CommsKind) =>
    `${subjectFor(kind)}\n\nProject: ${brief.projectName || "—"}\nRef: ${letterRef(kind)}\nDate: ${commsDate()}\n\nPlease find the above letter, issued by ${issuerName(kind)}. Open this link to view it:\n${letterShareUrl}`;

  async function exportCommsRegister(fmt: "docx" | "pdf") {
    setLetterBusy("register" + fmt);
    setPdfErr(null);
    try {
      const doc = assembleCommsRegister(brief);
      if (fmt === "docx") await generateDocx(doc, paperSize);
      else await exportPdf(doc, paperSize);
    } catch (e: any) {
      setPdfErr(`Register export failed: ${String(e?.message || e).slice(0, 120)}`);
    } finally {
      setLetterBusy(null);
    }
  }
  const removeComms = (id: string) => patch({ commsLog: (brief.commsLog || []).filter((e) => e.id !== id) });
  const noteComms = (id: string, note: string) => patch({ commsLog: (brief.commsLog || []).map((e) => (e.id === id ? { ...e, note } : e)) });

  async function draftWithAI() {
    if (!aiGoal.trim()) return;
    setAiBusy(true); setAiError(null);
    try {
      const res = await fetch("/api/draft", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ brief, instruction: aiGoal }) });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const data = (await res.json()) as { brief?: Partial<ProjectBrief> };
      if (data.brief) setBrief((b) => ({ ...b, ...data.brief }));
      setAiGoal("");
    } catch (e: any) {
      setAiError(`Drafting endpoint unavailable (${e.message}). Run the Worker with \`wrangler dev\` + ANTHROPIC_API_KEY, or edit fields manually.`);
    } finally { setAiBusy(false); }
  }

  async function onGenerate() {
    setGenBusy(true);
    setPdfErr(null);
    try {
      await generateDocx(contract, paperSize);
    } catch (e: any) {
      setPdfErr(`Word export failed: ${String(e?.message || e).slice(0, 120)}`);
    } finally { setGenBusy(false); }
  }
  async function onExportPdf() {
    setPdfBusy(true);
    setPdfErr(null);
    try {
      await exportPdf(contract, paperSize);
    } catch (e: any) {
      setPdfErr(`PDF export failed: ${String(e?.message || e).slice(0, 120)}`);
    } finally {
      setPdfBusy(false);
    }
  }

  const contractSeed: ContractSeed = {
    projectName: brief.projectName,
    reference: brief.contractReference,
    location: brief.projectLocation,
    currency: brief.contractPrice.currency,
    employer: brief.employer.name,
    contractor: brief.contractor.name,
    contractValue: brief.contractPrice.amount,
    atelierProjectId: brief.atelierProjectId,
  };
  const goToPayments = (link: boolean) => { setPayAutoLink(link); setMode("payments"); };

  // --- IPC tab ↔ ledger -------------------------------------------------------
  // Entering the IPC tab refreshes the ledger cache from the cloud so the
  // contract picker shows what the Payments screen shows.
  useEffect(() => {
    if (mode !== "ipc") return;
    refreshLedgerCache().then((d) => setLedgerProjects(d.projects));
  }, [mode]);
  // Resolve which ledger contract this project's certificates go under: the one
  // remembered on the project, else the ledger project linked to this contract.
  const linkedLedgerProject = (projects: PaymentProject[]) =>
    projects.find((p) => p.linkedContract && ((brief.contractReference && p.linkedContract.reference === brief.contractReference) || p.linkedContract.name === brief.projectName));
  useEffect(() => {
    const t = brief.ledgerTarget;
    if (t && ledgerProjects.some((p) => p.id === t.projectId && p.contracts.some((c) => c.id === t.contractId))) { setIpcTarget(t); return; }
    const proj = linkedLedgerProject(ledgerProjects);
    const prime = proj?.contracts.find((c) => c.kind === "prime") || proj?.contracts[0];
    setIpcTarget(proj && prime ? { projectId: proj.id, contractId: prime.id } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ledgerProjects, brief.ledgerTarget, brief.projectName, brief.contractReference]);
  const targetContract = useMemo(() => ledgerProjects.find((p) => p.id === ipcTarget?.projectId)?.contracts.find((c) => c.id === ipcTarget?.contractId) || null, [ledgerProjects, ipcTarget]);
  const setTarget = (t: { projectId: string; contractId: string }) => { setIpcTarget(t); patch({ ledgerTarget: t }); };
  const tfmt = (x: number) => `${targetContract?.currency || ipc.currency} ${x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  // Seed the certificate from the ledger contract: next number in its series,
  // its currency and retention, and what has already been certified under it.
  function fillFromLedger() {
    if (!targetContract) return;
    const series = ipc.kind === "advance" ? "advance" : ipc.kind === "final" ? "final" : "interim";
    const probe = { id: "__new__" } as PaymentCert;
    patchIpc({
      certificateNo: nextCertNumber(targetContract, series),
      currency: targetContract.currency || ipc.currency,
      retentionPercent: targetContract.retentionPercent ?? ipc.retentionPercent,
      previouslyCertified: previouslyCertifiedAuto(targetContract, probe),
      installmentIndex: series === "advance" ? targetContract.certificates.filter(isAdvanceCert).length + 1 : ipc.installmentIndex,
    });
  }
  // First time a target resolves while the form is still the untouched default,
  // fill it — so the tab opens on the right number without a click.
  const seededFrom = useRef<string | null>(null);
  useEffect(() => {
    if (!targetContract || ipcTouched.current || seededFrom.current === targetContract.id) return;
    seededFrom.current = targetContract.id;
    fillFromLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetContract]);

  // Financial position of this contract, read from the linked payments ledger.
  function contractLedgerPosition() {
    const dataL = loadPayments();
    const proj = dataL.projects.find((p) => p.linkedContract && ((brief.contractReference && p.linkedContract.reference === brief.contractReference) || p.linkedContract.name === brief.projectName));
    const prime = proj?.contracts.find((c) => c.kind === "prime") || proj?.contracts[0];
    const certs = prime?.certificates || [];
    // Advances (14.2) are repayable — excluded from certified works, retention, and remaining.
    const certified = certs.reduce((s, c) => s + (!isAdvanceCert(c) && isFinite(c.amount) ? c.amount : 0), 0);
    const advancePaid = certs.reduce((s, c) => s + (isAdvanceCert(c) && isFinite(c.amount) ? c.amount : 0), 0);
    const value = brief.contractPrice.amount;
    const retentionHeld = ((brief.retentionPercent || 0) / 100) * certified;
    const remaining = isFinite(value) ? value - certified : null;
    const pct = isFinite(value) && value ? (certified / value) * 100 : null;
    return { hasLedger: !!proj, certCount: certs.length, value: isFinite(value) ? value : null, cur: brief.contractPrice.currency, certified, advancePaid, retentionHeld, remaining, pct };
  }

  // One-click: read the contract's advance terms → create/open the linked ledger
  // → generate an advance payment certificate for each installment.
  async function generateAdvanceCerts() {
    const cur = brief.contractPrice.currency;
    const total = ((brief.advancePaymentPercent || 0) / 100) * (brief.contractPrice.amount || 0);
    let installments = brief.advanceSchedule && brief.advanceSchedule.length ? [...brief.advanceSchedule] : [];
    if (!installments.length) {
      if (brief.advanceFixedStart && isFinite(brief.advanceFixedStart.amount)) {
        installments = [{ label: "First installment", amount: brief.advanceFixedStart, trigger: "on provision of the Advance Payment Guarantee" }];
        const bal = total - brief.advanceFixedStart.amount;
        if ((brief.advanceInstallments || 1) > 1 && bal > 0.01) installments.push({ label: "Balance", amount: { amount: bal, currency: cur }, trigger: "per the Contract" });
      } else if (total > 0) {
        installments = [{ label: "Advance payment", amount: { amount: total, currency: cur }, trigger: "on provision of the Advance Payment Guarantee" }];
      }
    }
    if (!installments.length) { setLedgerMsg("Set the advance % (or a schedule) on the contract first."); setTimeout(() => setLedgerMsg(null), 3500); return; }

    // Resolve each installment's cash amount (fixed → % of contract → balance).
    const amounts: (number | null)[] = installments.map(() => null);
    let allocated = 0;
    installments.forEach((inst, i) => { if (inst.amount && isFinite(inst.amount.amount)) { amounts[i] = inst.amount.amount; allocated += inst.amount.amount; } });
    installments.forEach((inst, i) => { if (amounts[i] != null) return; if (inst.percent != null && inst.percent < (brief.advancePaymentPercent || 100)) { amounts[i] = (inst.percent / 100) * (brief.contractPrice.amount || 0); allocated += amounts[i]!; } });
    installments.forEach((inst, i) => { if (amounts[i] != null) return; amounts[i] = Math.max(0, total - allocated); allocated += amounts[i]!; });

    const n = installments.length;
    setLedgerBusy(true);
    let recorded: { projectId: string; contractId: string; party: string } | null = null;
    const res = await applyToLedger((data) => {
    const { proj, contract: prime } = resolveLedgerContract(data, cur);
    recorded = { projectId: proj.id, contractId: prime.id, party: prime.party };
    installments.forEach((inst, i) => {
      // One Advance Payment Certificate per installment, numbered UNDER the IPC
      // series (14.2 advance is certified through the Clause 14 mechanism). Uses
      // the same "IPC.A.n" scheme as the IPC (FIDIC) mode's advance certificate —
      // distinct from interim "IPC n" so the two never clash. Cert 1 is certified
      // against the Advance Payment Guarantee (14.2.1); later certs carry their own
      // trigger (e.g. the balance released on the Employer's bank financing).
      const number = `IPC.A.${i + 1}`;
      // Match — and so rename in place — certs from the earlier naming schemes, so
      // re-generating migrates an existing "Advance Payment Cert. 1 of 2" to IPC.A.1
      // rather than creating a duplicate.
      const existing = prime!.certificates.find(
        (c) => c.number === number || c.number === `Advance Payment Cert. ${i + 1} of ${n}` || c.number === `Advance ${i + 1} of ${n}`
      );
      const fidicPos = i === 0
        ? "First advance disbursement under Sub-Clause 14.2 [Advance Payment], certified against the Advance Payment Guarantee (Sub-Clause 14.2.1)"
        : `Advance disbursement ${i + 1} of ${n} under Sub-Clause 14.2 [Advance Payment]`;
      // Update in place when the cert already exists: keep everything the user
      // added (payments received, proof, signature, share link, notes,
      // acknowledgement, status, original date) and refresh only the generator-
      // owned fields (number, description, amount). Re-generating must never wipe
      // payment history or attachments.
      const base: PaymentCert = existing || { id: uid(), number, date: nowDate(), kind: "construction", description: "", amount: NaN, currency: cur, status: "authorised", coversAllOutstanding: false, createdAt: nowDate() };
      const cert: PaymentCert = {
        ...base,
        number,
        kind: "construction",
        description: `${fidicPos} — ${inst.label || `Installment ${i + 1}`}${inst.trigger ? `, payable ${inst.trigger}` : ""}.`,
        amount: amounts[i] ?? NaN,
        currency: cur,
        buildup: { ...(base.buildup || {}), advancePercent: brief.advancePaymentPercent, installmentIndex: i + 1, installmentCount: n },
      };
      prime.certificates = existing ? prime.certificates.map((c) => (c.id === existing.id ? cert : c)) : [...prime.certificates, cert];
    });
    });
    setLedgerBusy(false);
    setLedgerProjects(loadPayments().projects);
    const rec = recorded as { projectId: string; contractId: string; party: string } | null;
    if (rec) { setTarget({ projectId: rec.projectId, contractId: rec.contractId }); setPayFocus({ projectId: rec.projectId, contractId: rec.contractId }); }
    goToPayments(false);
    const parts = installments.map((inst, i) => `IPC.A.${i + 1}: ${cur} ${(amounts[i] ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${i === 0 ? " (on Advance Payment Guarantee)" : inst.trigger ? ` (${inst.trigger})` : ""}`).join("; ");
    setLedgerMsg(`Created ${n} advance IPC(s) under ${rec ? rec.party : "the contract"} — ${parts}.${res.synced ? "" : ` (${res.error})`}`);
    setTimeout(() => setLedgerMsg(null), 6000);
  }

  // The ledger contract to record under: the chosen target; else the project
  // linked to this contract (its prime contract); else create both.
  function resolveLedgerContract(data: { projects: PaymentProject[] }, currency: string) {
    let proj = ipcTarget ? data.projects.find((p) => p.id === ipcTarget.projectId) : undefined;
    if (!proj) proj = linkedLedgerProject(data.projects);
    if (!proj) { proj = projectFromContract(contractSeed); data.projects.push(proj); }
    let contract = ipcTarget ? proj.contracts.find((c) => c.id === ipcTarget.contractId) : undefined;
    if (!contract) contract = proj.contracts.find((c) => c.kind === "prime") || proj.contracts[0];
    if (!contract) { contract = { id: uid(), name: "Prime Construction Contract", kind: "prime", party: brief.contractor.name, currency, value: isFinite(brief.contractPrice.amount) ? brief.contractPrice.amount : undefined, retentionPercent: brief.retentionPercent, certificates: [] }; proj.contracts.push(contract); }
    return { proj, contract };
  }

  // Issue the FIDIC IPC into the payments ledger under the chosen contract —
  // through the sync-aware path, so it lands in the cloud ledger (not just this
  // browser's cache) and is there when the Payments screen opens.
  async function recordIpcInLedger() {
    const fin = (v?: number) => (v != null && isFinite(v) ? v : undefined);
    const amount = netCertified(brief, ipc);
    if (!isFinite(amount) || amount <= 0) { setLedgerMsg("Enter the amounts first — the certificate has nothing to certify yet."); setTimeout(() => setLedgerMsg(null), 3500); return; }
    const number = ipc.certificateNo || "";
    const isAdv = ipc.kind === "advance";
    const v = ipc.valuation || {};
    // Everything the build-up needs travels with the certificate, so the ledger
    // can print the same document and feed the next IPC's "previously certified".
    const buildup: CertBuildup = {
      statementRef: ipc.statementRef || undefined,
      statementDate: ipc.statementDate || undefined,
      periodFrom: ipc.periodFrom || undefined,
      periodTo: ipc.periodTo || undefined,
      engineerName: ipc.engineerName || brief.engineer?.name || undefined,
      paymentDueDays: fin(ipc.paymentDueDays ?? brief.paymentPeriodDays),
      ...(isAdv
        ? { advancePercent: fin(ipc.advancePercent), installmentIndex: fin(ipc.installmentIndex), installmentCount: fin(ipc.installmentCount), guaranteeRef: ipc.guaranteeRef || undefined }
        : {
            worksExecuted: fin(v.worksExecuted), materialsOnSite: fin(v.materialsOnSite), variations: fin(v.variations), provisionalSums: fin(v.provisionalSums), dayworks: fin(v.dayworks), priceAdjustment: fin(v.priceAdjustment), otherAdditions: fin(v.otherAdditions),
            retentionPercent: fin(ipc.retentionPercent ?? brief.retentionPercent), retentionLimitPercent: fin(ipc.retentionLimitPercent ?? brief.retentionCapPercent), retentionReleased: fin(ipc.retentionReleased),
            advanceRecoveredToDate: fin(ipc.advanceRecoveredToDate), previouslyCertified: fin(ipc.previouslyCertified), vatPercent: fin(ipc.vatPercent),
          }),
    };
    const description = isAdv
      ? `Advance disbursement${ipc.installmentIndex ? ` ${ipc.installmentIndex}${ipc.installmentCount ? ` of ${ipc.installmentCount}` : ""}` : ""} under Sub-Clause 14.2 [Advance Payment]${ipc.guaranteeRef ? `, against Advance Payment Guarantee ${ipc.guaranteeRef}` : ""}`
      : ipc.kind === "final"
        ? `Final payment — the Works to completion${ipc.statementRef ? `, Contractor's Final Statement ${ipc.statementRef}` : ""}`
        : `Works executed and valued to ${ipc.periodTo || ipc.date || "date"}${ipc.statementRef ? `, Contractor's Statement ${ipc.statementRef}` : ""}`;
    setLedgerBusy(true);
    let recorded: { projectId: string; contractId: string; certId: string; party: string; existed: boolean } | null = null;
    const res = await applyToLedger((data) => {
      const { proj, contract } = resolveLedgerContract(data, ipc.currency);
      const existing = number ? contract.certificates.find((c) => c.number === number) : undefined;
      const cert: PaymentCert = {
        ...(existing || { id: uid(), status: "authorised", coversAllOutstanding: false, createdAt: nowDate() }),
        number,
        date: ipc.date || nowDate(),
        kind: "construction",
        description,
        amount,
        currency: ipc.currency,
        invoiceRef: ipc.statementRef || undefined,
        buildup,
      } as PaymentCert;
      contract.certificates = existing ? contract.certificates.map((c) => (c.id === existing.id ? cert : c)) : [...contract.certificates, cert];
      recorded = { projectId: proj.id, contractId: contract.id, certId: cert.id, party: contract.party, existed: !!existing };
    });
    setLedgerBusy(false);
    setLedgerProjects(loadPayments().projects);
    const r = recorded as { projectId: string; contractId: string; certId: string; party: string; existed: boolean } | null;
    if (r) {
      setTarget({ projectId: r.projectId, contractId: r.contractId });
      setPayFocus({ projectId: r.projectId, contractId: r.contractId, certId: r.certId });
    }
    setLedgerMsg(`${r?.existed ? "Re-issued (updated)" : "Issued"} ${number || "certificate"} under ${r?.party || "the contract"}${res.synced ? " — in the ledger." : ` — ${res.error}.`}`);
    setTimeout(() => setLedgerMsg(null), 6000);
    goToPayments(false);
  }

  // Password-reset page (from the emailed link) — works before anything else.
  if (resetToken) return <AuthGate resetToken={resetToken} onDone={(email) => { setUser(email); try { window.history.replaceState(null, "", window.location.pathname); } catch {} setEntered(true); }} />;
  if (!entered) return <Landing onEnter={enter} />;
  // Owner interfaces require login; client tracking links (?ledger=) bypass it.
  if (!deepLink) {
    if (user === undefined) return <div className="landing landing--ready"><div className="ld-main" style={{ paddingTop: 120 }}><p className="ld-lede">Loading your studio…</p></div></div>;
    if (!user) return <AuthGate onDone={(email) => setUser(email)} />;
  }
  if (contractGate) {
    const draft = loadCurrent();
    return (
      <ContractGate
        saved={saved}
        draftName={draft?.projectName || undefined}
        onOpen={(name) => { doLoadNamed(name); setContractGate(false); }}
        onContinue={() => { const d = loadCurrent(); if (d) setBrief(d); setContractGate(false); }}
        onNew={() => { setBrief(blankProject()); setContractGate(false); }}
        onNewArchitecture={() => { setBrief(blankArchitectureProject()); setContractGate(false); }}
        onSample={() => { setBrief(SAMPLE_PROJECT); setContractGate(false); }}
        onHome={goHome}
      />
    );
  }

  return (
    <div className="app" style={mode === "payments" || mode === "scan" ? { gridTemplateColumns: "1fr" } : undefined}>
      <div className="pane form">
        <div className="topbar">
          <div className="brand brand-home" onClick={goHome} title="Back to the studio home">Studio<span className="dot"> · </span>Contract</div>
          {user && <button className="signout" onClick={doSignOut} title={`Signed in as ${user}`}>Sign out</button>}
          <div className="modeswitch">
            <button className={mode === "contract" ? "on" : ""} onClick={() => setMode("contract")}>Contract</button>
            <button className={mode === "ipc" ? "on" : ""} onClick={() => setMode("ipc")}>IPC (FIDIC)</button>
            <button className={mode === "payments" ? "on" : ""} onClick={() => goToPayments(false)}>Payments</button>
            <button className={mode === "scan" ? "on" : ""} onClick={() => setMode("scan")}>Conflict scan</button>
            <button className={mode === "review" ? "on" : ""} onClick={() => setMode("review")}>Review{brief.review?.some((c) => c.status === "open") ? ` (${brief.review.filter((c) => c.status === "open").length})` : ""}</button>
          </div>
        </div>

        <div className="content">
          {mode === "payments" ? (
            <PaymentsApp contractSeed={contractSeed} autoLink={payAutoLink} focus={payFocus} />
          ) : mode === "scan" ? (
            <ConflictScanner
              renderOutputPdf={() => renderPdfBlob(assemble(brief), paperSize)}
              outputLabel={`${brief.projectName || "This contract"} (output PDF)`}
              brief={brief}
              onApplyFixes={applyFixes}
            />
          ) : (<>
          {/* What is loaded right now — the project, its parties, and (in the IPC
              tab) the ledger contract certificates are recorded under. */}
          <div className="workbar">
            <div className="wb-main">
              <span className="wb-k">Working on</span>
              <b className="wb-name">{brief.projectName || "Untitled project"}</b>
              {brief.contractReference && <span className="wb-ref">{brief.contractReference}</span>}
              <span className="wb-std">{STANDARDS.find((s) => s.id === brief.standard)?.short || brief.standard}</span>
            </div>
            <div className="wb-sub">
              <span>{design ? "Owner" : "Employer"} <b>{brief.employer.name || "—"}</b></span>
              <span>{design ? "Architect" : "Contractor"} <b>{brief.contractor.name || "—"}</b></span>
              {brief.projectLocation && <span className="muted">{brief.projectLocation}</span>}
            </div>
            {mode === "ipc" && (
              <div className="wb-sub wb-ledger">
                <span className="wb-k">Ledger</span>
                {targetContract ? (
                  <span>{ledgerProjects.find((p) => p.id === ipcTarget?.projectId)?.name} › <b>{targetContract.name}</b> — {targetContract.party}</span>
                ) : (
                  <em className="muted">no ledger contract chosen yet — pick one under “Record under” below</em>
                )}
              </div>
            )}
          </div>
          <div className="projbar">
            <button className="dark sm" onClick={doSave} title="Save this contract (syncs to your account when signed in)">💾 Save</button>
            <select
              className="sm"
              value=""
              onChange={(e) => { if (e.target.value) doLoadNamed(e.target.value); e.currentTarget.selectedIndex = 0; }}
              title="Open a saved project"
            >
              <option value="">Open saved…{saved.length ? "" : " (none)"}</option>
              {saved.map((s) => (
                <option key={s.name} value={s.name}>{s.name}</option>
              ))}
            </select>
            <button className="link" onClick={() => exportJson(brief)} title="Download project as .json">Export</button>
            <button className="link" onClick={() => importRef.current?.click()} title="Load a .json project file">Import</button>
            <input ref={importRef} type="file" accept="application/json,.json" style={{ display: "none" }} onChange={(e) => doImport(e.target.files?.[0])} />
            <select
              className="sm"
              value=""
              onChange={(e) => {
                const v = e.target.value;
                if (v === "atelier") sendProjectToAtelier(brief);
                else if (v === "bid") sendToStudio(brief, "bid");
                else if (v === "pm") sendToStudio(brief, "pm");
                e.currentTarget.selectedIndex = 0;
              }}
              title="Send this project to another app (carries the project details and drawing register across)"
            >
              <option value="">Send to…</option>
              <option value="atelier">→ Atelier (studio cockpit)</option>
              <option value="bid">→ Bid Manager</option>
              <option value="pm">→ Project Manager</option>
            </select>
            {studioLink && <a className="link btnlink" href={studioLink.url} target="_blank" rel="noreferrer" title={`Linked ${studioLink.target === "bid" ? "tender" : "project"} ${studioLink.code}`}>⛓ {studioLink.code || "Studio job"}</a>}
            <div className="spacer" />
            <button className="link" onClick={() => goToPayments(true)} title="Open the linked payments ledger for this contract">⛓ Track payments →</button>
            <button className="link" onClick={() => setBrief(SAMPLE_PROJECT)}>Sample</button>
            {savedTick && <span className="proj-tick">{savedTick}</span>}
          </div>
          {saved.length > 0 && (
            <div className="saved-chips">
              {saved.map((s) => (
                <span className={`chip${s.name === brief.projectName ? " on" : ""}`} key={s.name} title={s.name === brief.projectName ? "Currently loaded" : undefined}>
                  <button className="chip-load" onClick={() => doLoadNamed(s.name)} title={s.name === brief.projectName ? "Currently loaded" : `Saved ${s.savedAt} — click to open`}>{s.name === brief.projectName ? "● " : ""}{s.name}</button>
                  <button className="chip-x" onClick={() => doDeleteNamed(s.name)} title="Delete">✕</button>
                </span>
              ))}
            </div>
          )}

          {mode === "review" ? (
            <ReviewPanel
              comments={reviewList}
              anchors={reviewAnchors}
              onAdd={reviewAdd}
              onReply={reviewReply}
              onStatus={reviewStatus}
              onRemove={reviewRemove}
              banner={
                <div className="share-box">
                  {brief.reviewId ? (
                    <>
                      <div className="lbl">Shared review link (send to the other party)</div>
                      <div className="share-row">
                        <input readOnly value={reviewUrl} onFocus={(e) => e.currentTarget.select()} />
                        <button onClick={copyLink}>{copied ? "Copied ✓" : "Copy"}</button>
                        <a className="btnlink" href={reviewUrl} target="_blank" rel="noreferrer">Open</a>
                      </div>
                      <div className="share-row">
                        <ShareButtons url={reviewUrl} title={brief.projectName} emails={[brief.employer?.email, brief.contractor?.email, brief.engineer?.email]} />
                      </div>
                      <div className="share-actions">
                        <button onClick={pushDocUpdate} disabled={shareBusy}>{shareBusy ? <span className="spinner dark-spin" /> : "Push current document to link"}</button>
                        <span className="hint" style={{ margin: 0 }}>Live: everyone on the link sees comments update automatically.</span>
                      </div>
                    </>
                  ) : (
                    <div className="share-actions">
                      <button className="dark" onClick={createShareLink} disabled={shareBusy}>{shareBusy ? <span className="spinner" /> : "🔗 Create shareable live link"}</button>
                      <span className="hint" style={{ margin: 0 }}>Creates one link both parties comment on live.</span>
                    </div>
                  )}
                  {shareErr && <div className="hint" style={{ color: "#a3402f" }}>{shareErr}</div>}
                </div>
              }
            />
          ) : mode === "contract" ? (
            <>
              <div className="notice">{assemble(brief).notice}</div>

              <div className="group">
                <div className="glabel">Standard</div>
                <select value={brief.standard} onChange={(e) => patch({ standard: e.target.value as StandardId })}>
                  {STANDARDS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </div>

              <div className="group">
                <div className="glabel">AI assistant {aiReady === true && <span className="ai-badge ok">● ready</span>}{aiReady === false && <span className="ai-badge off">● offline</span>}</div>
                <AssistantChat brief={brief} onPatch={applyAssistPatch} aiReady={aiReady} />
              </div>

              <div className="group">
                <div className="glabel">Import from drawings / documents</div>
                <PdfImport onMerge={(p) => setBrief((b) => ({ ...b, ...p }))} />
              </div>

              <div className="group">
                <div className="glabel">Project</div>
                <label><span className="lbl">Project name</span><input value={brief.projectName} onChange={(e) => patch({ projectName: e.target.value })} /></label>
                <div className="row">
                  <label><span className="lbl">Location</span><input value={brief.projectLocation} onChange={(e) => patch({ projectLocation: e.target.value })} /></label>
                  <label><span className="lbl">Contract no.</span><input value={brief.contractReference || ""} onChange={(e) => patch({ contractReference: e.target.value })} /></label>
                </div>
                <label><span className="lbl">Description of the Works</span></label>
                <AiTextArea value={brief.worksDescription} onChange={(v) => patch({ worksDescription: v })} label="Description of the Works" context={{ projectName: brief.projectName, location: brief.projectLocation }} />
              </div>

              <div className="group">
                <div className="glabel">Parties</div>
                {(["employer", "contractor", "engineer"] as const).map((role) => (
                  <div key={role} style={{ marginBottom: 12 }}>
                    <div className="subhead">{role === "employer" ? (design ? "Owner / Client" : "Employer") : role === "engineer" ? (design ? "Consultant (optional)" : "Engineer / Consultant") : (design ? "Architect" : "Contractor")}</div>
                    <div className="row">
                      <label><span className="lbl">Name</span><input value={brief[role]?.name || ""} onChange={(e) => patchParty(role, { name: e.target.value })} /></label>
                      <label><span className="lbl">Address</span><input value={brief[role]?.address || ""} onChange={(e) => patchParty(role, { address: e.target.value })} /></label>
                    </div>
                    {role !== "engineer" && (
                      <>
                        <div className="row">
                          <label><span className="lbl">Signatory</span><input value={brief[role]?.representative || ""} onChange={(e) => patchParty(role, { representative: e.target.value })} /></label>
                          <label><span className="lbl">Signatory title</span><input value={brief[role]?.representativeTitle || ""} onChange={(e) => patchParty(role, { representativeTitle: e.target.value })} /></label>
                        </div>
                        <div className="row">
                          <label><span className="lbl">Witness name</span><input value={brief[role]?.witnessName || ""} onChange={(e) => patchParty(role, { witnessName: e.target.value })} /></label>
                          <label><span className="lbl">Witness address</span><input value={brief[role]?.witnessAddress || ""} onChange={(e) => patchParty(role, { witnessAddress: e.target.value })} /></label>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className="group">
                <div className="glabel">Title-page &amp; preparation</div>
                <div className="row">
                  <label><span className="lbl">Cover date</span><input value={brief.coverDate || ""} onChange={(e) => patch({ coverDate: e.target.value })} placeholder="19 April 2025" /></label>
                  <label><span className="lbl">Prepared by (name)</span><input value={brief.preparedBy?.name || ""} onChange={(e) => patch({ preparedBy: { ...(brief.preparedBy || {}), name: e.target.value } })} /></label>
                </div>
                <div className="row">
                  <label><span className="lbl">Prepared by (firm)</span><input value={brief.preparedBy?.org || ""} onChange={(e) => patch({ preparedBy: { ...(brief.preparedBy || {}), org: e.target.value } })} /></label>
                  <label><span className="lbl">Firm role</span><input value={brief.preparedBy?.role || ""} onChange={(e) => patch({ preparedBy: { ...(brief.preparedBy || {}), role: e.target.value } })} /></label>
                </div>
                <label><span className="lbl">Firm address</span><input value={brief.preparedBy?.address || ""} onChange={(e) => patch({ preparedBy: { ...(brief.preparedBy || {}), address: e.target.value } })} /></label>
              </div>

              <div className="group">
                <div className="glabel">Commercial &amp; time</div>
                <div className="row-3">
                  <label><span className="lbl">{design ? (brief.designFeeBasis === "percentage" || brief.designFeeBasis === "hourly" ? "Fee ref. amount" : "Architect's fee (stipulated)") : "Contract price"}</span><input type="number" value={numStr(brief.contractPrice.amount)} onChange={(e) => patch({ contractPrice: { ...brief.contractPrice, amount: num(e.target.value) } })} /></label>
                  <label><span className="lbl">Currency</span><input value={brief.contractPrice.currency} onChange={(e) => patch({ contractPrice: { ...brief.contractPrice, currency: e.target.value } })} /></label>
                  <label><span className="lbl">VAT treatment</span>
                    <select value={brief.priceVat || "exclusive"} onChange={(e) => patch({ priceVat: e.target.value as "exclusive" | "inclusive" })}>
                      <option value="exclusive">VAT exclusive</option>
                      <option value="inclusive">VAT inclusive</option>
                    </select>
                  </label>
                </div>
                {design && (
                  <>
                    <div className="row">
                      <label><span className="lbl">Fee basis</span>
                        <select value={brief.designFeeBasis || "stipulated"} onChange={(e) => patch({ designFeeBasis: e.target.value as "percentage" | "stipulated" | "hourly" })}>
                          <option value="stipulated">Stipulated sum</option>
                          <option value="percentage">% of Cost of the Work</option>
                          <option value="hourly">Hourly (Exhibit B)</option>
                        </select>
                      </label>
                      {brief.designFeeBasis === "percentage" && (
                        <label><span className="lbl">Fee %</span><input type="number" value={numStr(brief.designFeePercent)} onChange={(e) => patch({ designFeePercent: num(e.target.value) })} placeholder="e.g. 8" /></label>
                      )}
                    </div>
                    <div className="row">
                      <label><span className="lbl">Estimated Cost of the Work</span><input type="number" value={numStr(brief.estimatedConstructionCost?.amount)} onChange={(e) => patch({ estimatedConstructionCost: { amount: num(e.target.value), currency: brief.estimatedConstructionCost?.currency || brief.contractPrice.currency } })} placeholder="Owner's construction budget" /></label>
                      <label><span className="lbl">Reimbursables</span><input value={brief.reimbursablesNote || ""} onChange={(e) => patch({ reimbursablesNote: e.target.value })} placeholder="at cost plus 10%" /></label>
                    </div>
                    <div className="field">
                      <span className="lbl">Basic Services (phases included)</span>
                      <div className="phase-checks">
                        {[["SD", "Schematic Design"], ["DD", "Design Development"], ["CD", "Construction Documents"], ["BN", "Bidding / Negotiation"], ["CA", "Construction Administration"]].map(([k, name]) => {
                          const sel = brief.architectServices && brief.architectServices.length ? brief.architectServices : ["SD", "DD", "CD", "BN", "CA"];
                          const on = sel.includes(k);
                          return (
                            <label key={k} className="phase-check"><input type="checkbox" checked={on} onChange={() => { const base = brief.architectServices && brief.architectServices.length ? [...brief.architectServices] : ["SD", "DD", "CD", "BN", "CA"]; const next = on ? base.filter((x) => x !== k) : [...base, k]; patch({ architectServices: next }); }} /> {name}</label>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
                <label style={{ marginTop: -2 }}><span className="lbl">{design ? "Project schedule" : "Time for completion"}</span><input value={brief.timeForCompletion} onChange={(e) => patch({ timeForCompletion: e.target.value })} placeholder={design ? "e.g. 6 months to CDs" : "30 Months"} /></label>
                {!design && (
                <div className="row">
                  <label><span className="lbl">Rate of exchange (Sub-Clause 14.15)</span>
                    <select value={brief.exchangeRateBasis || "prevailing"} onChange={(e) => patch({ exchangeRateBasis: e.target.value as "prevailing" | "fixed" })}>
                      <option value="prevailing">Prevailing rate (market, at date of payment)</option>
                      <option value="fixed">Static rate (fixed in the Contract)</option>
                    </select>
                  </label>
                  {(brief.exchangeRateBasis || "prevailing") === "fixed" ? (
                    <label><span className="lbl">Fixed rate(s)</span><input value={brief.fixedExchangeRate || ""} onChange={(e) => patch({ fixedExchangeRate: e.target.value })} placeholder="e.g. USD 1 = GYD 209" /></label>
                  ) : (
                    <label><span className="lbl">Rate source <span className="lbl-opt">(optional)</span></span><input value={brief.exchangeRateSource || ""} onChange={(e) => patch({ exchangeRateSource: e.target.value })} placeholder="e.g. Bank of Guyana selling rate" /></label>
                  )}
                </div>
                )}
                <label>
                  <span className="lbl">Contract price in words <span className="ai-badge ok" style={{ fontWeight: 500 }}>auto from amount</span></span>
                  <input readOnly value={amountToWords(brief.contractPrice) || "—"} style={{ background: "#f4f3ef", color: "var(--muted)" }} title="Always derived from the amount and currency, so it can never disagree with the figure" />
                </label>
                <div className="row-3">
                  <label><span className="lbl">Defects period</span><input value={brief.defectsNotification} onChange={(e) => patch({ defectsNotification: e.target.value })} placeholder="12 months" /></label>
                  <label><span className="lbl">Perf. security %</span><input type="number" value={numStr(brief.performanceSecurityPercent)} onChange={(e) => patch({ performanceSecurityPercent: num(e.target.value) })} /></label>
                  <label><span className="lbl">Advance %</span><input type="number" value={numStr(brief.advancePaymentPercent)} onChange={(e) => patch({ advancePaymentPercent: num(e.target.value) })} /></label>
                </div>
                <div className="row-3">
                  <label><span className="lbl">Retention %</span><input type="number" value={numStr(brief.retentionPercent)} onChange={(e) => patch({ retentionPercent: num(e.target.value) })} /></label>
                  <label><span className="lbl">Delay dmg / day</span><input type="number" value={numStr(brief.delayDamagesPerDay?.amount)} onChange={(e) => patch({ delayDamagesPerDay: { amount: num(e.target.value), currency: brief.delayDamagesPerDay?.currency || brief.contractPrice.currency } })} /></label>
                  <label><span className="lbl">Delay cap %</span><input type="number" value={numStr(brief.delayDamagesCapPercent)} onChange={(e) => patch({ delayDamagesCapPercent: num(e.target.value) })} /></label>
                </div>
                <div className="row-3">
                  <label><span className="lbl">Min. IPC</span><input type="number" value={numStr(brief.minimumIpc?.amount)} onChange={(e) => patch({ minimumIpc: { amount: num(e.target.value), currency: brief.minimumIpc?.currency || brief.contractPrice.currency } })} /></label>
                  <label><span className="lbl">Payment period (days)</span><input type="number" value={numStr(brief.paymentPeriodDays)} onChange={(e) => patch({ paymentPeriodDays: num(e.target.value) })} /></label>
                  <label><span className="lbl">O&amp;P on prov. sums %</span><input type="number" value={numStr(brief.overheadProfitPercent)} onChange={(e) => patch({ overheadProfitPercent: num(e.target.value) })} /></label>
                </div>
                <div className="row-3">
                  <label><span className="lbl">Advance fixed start</span><input type="number" value={numStr(brief.advanceFixedStart?.amount)} onChange={(e) => patch({ advanceFixedStart: { amount: num(e.target.value), currency: brief.advanceFixedStart?.currency || brief.contractPrice.currency } })} placeholder="e.g. 700000" /></label>
                  <label><span className="lbl">Advance ccy</span><input value={brief.advanceFixedStart?.currency || brief.contractPrice.currency} onChange={(e) => patch({ advanceFixedStart: { amount: brief.advanceFixedStart?.amount ?? NaN, currency: e.target.value } })} /></label>
                  <label><span className="lbl">Installments</span><input type="number" value={numStr(brief.advanceInstallments)} onChange={(e) => patch({ advanceInstallments: num(e.target.value) })} /></label>
                </div>
                <label><span className="lbl">Advance repayment</span><input value={brief.advanceRepayment || ""} onChange={(e) => patch({ advanceRepayment: e.target.value })} /></label>

                <div className="subhead" style={{ marginTop: 8 }}>Advance disbursement schedule ({advSched().length})</div>
                <div className="hint">Each installment renders as a lettered sub-clause (a)/(b)… under Advance Payment 14.2 in Part B. Enter a fixed amount or a %, plus the trigger.</div>
                {advSched().map((inst, i) => (
                  <div className="card" key={i}>
                    <div className="row-3">
                      <label style={{ margin: 0 }}><span className="lbl">Label</span><input value={inst.label || ""} onChange={(e) => updAdvInst(i, { label: e.target.value })} placeholder={`Installment ${i + 1}`} /></label>
                      <label style={{ margin: 0 }}><span className="lbl">Fixed amount</span><input type="number" value={numStr(inst.amount?.amount)} onChange={(e) => updAdvInst(i, { amount: { amount: num(e.target.value), currency: inst.amount?.currency || brief.contractPrice.currency } })} /></label>
                      <label style={{ margin: 0 }}><span className="lbl">or %</span><input type="number" value={numStr(inst.percent)} onChange={(e) => updAdvInst(i, { percent: num(e.target.value) })} /></label>
                    </div>
                    <label style={{ margin: "8px 0 0" }}><span className="lbl">Trigger / condition</span><input value={inst.trigger || ""} onChange={(e) => updAdvInst(i, { trigger: e.target.value })} placeholder="e.g. on release of funds from the financing bank, within 8 weeks of mobilisation" /></label>
                    <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                      <button className="iconbtn" onClick={() => moveAdvInst(i, -1)} disabled={i === 0}>↑</button>
                      <button className="iconbtn" onClick={() => moveAdvInst(i, 1)} disabled={i === advSched().length - 1}>↓</button>
                      <button className="link" onClick={() => rmAdvInst(i)}>Remove</button>
                    </div>
                  </div>
                ))}
                <button onClick={addAdvInst} style={{ marginTop: 4 }}>+ Add installment</button>
                {advProvCount > 0 && (
                  <div className="notice" style={{ marginTop: 10, color: "#a3552f" }}>
                    ⚠ Found {advProvCount} stored advance-payment clause(s) in your Special Conditions / narrative that conflict with the schedule above — this is what prints the "three (3) installments … Appendix to Tender" text. The schedule above is the single source; remove them once and it won't come back.
                    <div style={{ marginTop: 6 }}><button onClick={clearAdvanceProvisions}>Remove {advProvCount} conflicting advance clause(s)</button></div>
                  </div>
                )}
              </div>

              <div className="group">
                <div className="glabel">Legal &amp; dispute board</div>
                <div className="row">
                  <label><span className="lbl">Governing law</span><input value={brief.governingLaw} onChange={(e) => patch({ governingLaw: e.target.value })} /></label>
                  <label><span className="lbl">Ruling language</span><input value={brief.rulingLanguage} onChange={(e) => patch({ rulingLanguage: e.target.value })} /></label>
                </div>
                <div className="row-3">
                  <label><span className="lbl">Dispute board</span>
                    <select value={brief.daab?.kind || "DAAB"} onChange={(e) => patch({ daab: { ...(brief.daab || { members: 3 }), kind: e.target.value as "DAAB" | "DRB" } })}>
                      <option value="DAAB">DAAB (FIDIC default)</option>
                      <option value="DRB">DRB</option>
                    </select>
                  </label>
                  <label><span className="lbl">Board members</span><input type="number" value={numStr(brief.daab?.members)} onChange={(e) => patch({ daab: { ...(brief.daab || { members: 3 }), members: num(e.target.value) } })} /></label>
                  <label><span className="lbl">Appt. (weeks)</span><input type="number" value={numStr(brief.daab?.appointmentWeeks)} onChange={(e) => patch({ daab: { ...(brief.daab || { members: 3 }), appointmentWeeks: num(e.target.value) } })} /></label>
                  <label><span className="lbl">Addenda (comma)</span><input value={(brief.addenda || []).join(", ")} onChange={(e) => patch({ addenda: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} /></label>
                </div>
                <label><span className="lbl">DAAB registration body</span><input value={brief.daab?.registrationBody || ""} onChange={(e) => patch({ daab: { ...(brief.daab || { members: 3 }), registrationBody: e.target.value } })} /></label>
              </div>

              {!design && (
                <div className="group">
                  <div className="glabel">Letters &amp; notices</div>
                  <div className="row-3">
                    <label><span className="lbl">Letter date</span><input value={brief.letterDate || ""} onChange={(e) => patch({ letterDate: e.target.value })} placeholder={brief.coverDate || "15 July 2026"} /></label>
                    <label><span className="lbl">Site handover date</span><input value={brief.siteHandoverDate || ""} onChange={(e) => patch({ siteHandoverDate: e.target.value })} placeholder={brief.commencementDate || "date of possession"} /></label>
                    <label><span className="lbl">Extent handed over</span><input value={brief.siteHandoverExtent || ""} onChange={(e) => patch({ siteHandoverExtent: e.target.value })} placeholder="the whole of the Site" /></label>
                  </div>
                  <label style={{ marginTop: -2 }}><span className="lbl">Programme due <span className="lbl-opt">(Sub-Clause 8.3)</span></span><input value={brief.programmeDueDate || ""} onChange={(e) => patch({ programmeDueDate: e.target.value })} placeholder="within 28 days of the Commencement Date" /></label>
                  {/* Same rows as the action-bar drawer, from one list, so the two
                      can't drift apart. */}
                  {LETTERS.map((l) => (
                    <div className="letter-row" key={l.kind}>
                      <span className="letter-name">{l.name} <span className="letter-ref">{l.clause}</span></span>
                      <button onClick={() => genLetter(l.kind, "docx")} disabled={!!letterBusy}>{letterBusy === l.kind + "docx" ? <span className="spinner dark-spin" /> : "⤓ Word"}</button>
                      <button onClick={() => genLetter(l.kind, "pdf")} disabled={!!letterBusy}>{letterBusy === l.kind + "pdf" ? <span className="spinner dark-spin" /> : "⤓ PDF"}</button>
                      <button onClick={() => { setPanel("letters"); doShareLetter(l.kind); }} disabled={!!letterBusy} title="Create a live link to send by WhatsApp or email">{letterBusy === l.kind + "share" ? <span className="spinner dark-spin" /> : "🔗 Share"}</button>
                    </div>
                  ))}

                  <div className="subhead" style={{ marginTop: 14 }}>Letter to the Contractor</div>
                  <div className="row">
                    <label><span className="lbl">Subject</span><input value={letterSubject} onChange={(e) => setLetterSubject(e.target.value)} placeholder="e.g. Defective blockwork at Level 3 — rectification required" /></label>
                    <label><span className="lbl">Issued by</span>
                      <select value={letterFrom} onChange={(e) => setLetterFrom(e.target.value as "engineer" | "employer")}>
                        <option value="engineer">Engineer{brief.engineer?.name?.trim() ? "" : " (falls back to Employer)"}</option>
                        <option value="employer">Employer</option>
                      </select>
                    </label>
                  </div>
                  <AiTextArea
                    value={letterBody}
                    onChange={setLetterBody}
                    label={`Body of a formal contract letter to the Contractor about: ${letterSubject || "(set a subject above)"}`}
                    context={{ projectName: brief.projectName, subject: letterSubject, contractor: brief.contractor?.name, employer: brief.employer?.name, standard: brief.standard }}
                  />
                  <div className="letter-row">
                    <span className="letter-name muted" style={{ fontSize: 12 }}>Blank lines start a new paragraph; lines beginning “-” or “1.” become a list.</span>
                    <button onClick={() => genLetter("letter", "docx")} disabled={!!letterBusy || !letterSubject.trim() || !letterBody.trim()}>{letterBusy === "letterdocx" ? <span className="spinner dark-spin" /> : "⤓ Word"}</button>
                    <button onClick={() => genLetter("letter", "pdf")} disabled={!!letterBusy || !letterSubject.trim() || !letterBody.trim()}>{letterBusy === "letterpdf" ? <span className="spinner dark-spin" /> : "⤓ PDF"}</button>
                    <button onClick={() => { setPanel("letters"); doShareLetter("letter"); }} disabled={!!letterBusy || !letterSubject.trim() || !letterBody.trim()} title="Create a live link to send by WhatsApp or email">{letterBusy === "lettershare" ? <span className="spinner dark-spin" /> : "🔗 Share"}</button>
                  </div>
                  <div className="hint">
                    All built from this contract's parties, dates and terms, on the same letterhead with a Contractor acknowledgement to sign and return. Handover gives possession under 2.1; the Notice to Proceed sets the Commencement Date and lists the conditions precedent (security, insurances, programme{brief.advancePaymentPercent != null || brief.advanceFixedStart ? ", advance guarantee" : ""}); the Programme request calls up the 8.3 submission. Notices are issued by the {brief.engineer?.name?.trim() ? "Engineer" : "Employer"}. Each one is referenced in sequence and logged in the register below.
                  </div>
                </div>
              )}

              {!design && (
                <div className="group">
                  <div className="glabel">Communications ledger ({(brief.commsLog || []).length})</div>
                  {(brief.commsLog || []).length === 0 ? (
                    <div className="hint" style={{ margin: 0 }}>Every letter or notice you generate above is logged here automatically — reference, date, type and subject — building a register of everything issued to the Contractor.</div>
                  ) : (
                    <>
                      <div className="comms-table">
                        <div className="comms-row comms-head">
                          <span>Ref</span><span>Date</span><span>Type</span><span>Subject</span><span>Format</span><span>Note</span><span />
                        </div>
                        {(brief.commsLog || []).map((e) => (
                          <div className="comms-row" key={e.id}>
                            <span className="comms-ref">{e.ref}</span>
                            <span>{e.date}</span>
                            <span>{COMMS_META[e.kind]?.label || e.kind}{COMMS_META[e.kind]?.clause ? <span className="letter-ref"> {COMMS_META[e.kind].clause}</span> : null}</span>
                            <span className="comms-subj" title={e.subject}>{e.subject}</span>
                            <span className="muted">{e.format || "—"}</span>
                            <input className="comms-note" value={e.note || ""} onChange={(ev) => noteComms(e.id, ev.target.value)} placeholder="ack / response due…" />
                            <button className="chip-x" onClick={() => removeComms(e.id)} title="Remove from register">✕</button>
                          </div>
                        ))}
                      </div>
                      <div className="letter-row" style={{ marginTop: 8 }}>
                        <span className="letter-name">Register of Communications <span className="letter-ref">all {(brief.commsLog || []).length} issued</span></span>
                        <button onClick={() => exportCommsRegister("docx")} disabled={!!letterBusy}>{letterBusy === "registerdocx" ? <span className="spinner dark-spin" /> : "⤓ Word"}</button>
                        <button onClick={() => exportCommsRegister("pdf")} disabled={!!letterBusy}>{letterBusy === "registerpdf" ? <span className="spinner dark-spin" /> : "⤓ PDF"}</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="group">
                <div className="glabel">Special conditions — narrative ({brief.narrative.length})</div>
                <div className="hint">Part B project narrative (The Project, Parties, Site Conditions, Description of Works, Project Terms…).</div>
                {brief.narrative.map((n, i) => (
                  <div className="card" key={i}>
                    <div className="row" style={{ gridTemplateColumns: "60px 1fr" }}>
                      <label style={{ margin: 0 }}><span className="lbl">Marker</span><input value={n.marker || ""} onChange={(e) => updNarr(i, { marker: e.target.value })} placeholder="A" /></label>
                      <label style={{ margin: 0 }}><span className="lbl">Heading</span><input value={n.heading} onChange={(e) => updNarr(i, { heading: e.target.value })} /></label>
                    </div>
                    <label style={{ margin: "8px 0 0" }}><span className="lbl">Text</span></label>
                    <AiTextArea value={n.text} onChange={(v) => updNarr(i, { text: v })} label={`Special Conditions narrative — ${n.heading || "item"}`} context={{ projectName: brief.projectName, heading: n.heading }} />
                    <button className="link" onClick={() => rmNarr(i)} style={{ marginTop: 6 }}>Remove</button>
                  </div>
                ))}
                <button onClick={addNarr}>+ Add narrative item</button>
              </div>

              <div className="group">
                <div className="glabel">Special conditions — clause amendments ({brief.specialProvisions.length})</div>
                {brief.specialProvisions.map((sp, i) => (
                  <div className="card" key={i}>
                    <div className="row-3">
                      <label style={{ margin: 0 }}><span className="lbl">Clause</span><input value={sp.clauseRef || ""} onChange={(e) => updSP(i, { clauseRef: e.target.value })} placeholder="4.2" /></label>
                      <label style={{ margin: 0 }}><span className="lbl">Kind</span>
                        <select value={sp.kind || "add"} onChange={(e) => updSP(i, { kind: e.target.value as SpecialProvision["kind"] })}>
                          <option value="add">Add</option><option value="amend">Amend</option><option value="replace">Replace</option><option value="delete">Delete</option>
                        </select>
                      </label>
                      <label style={{ margin: 0 }}><span className="lbl">Heading</span><input value={sp.heading} onChange={(e) => updSP(i, { heading: e.target.value })} /></label>
                    </div>
                    <label style={{ margin: "8px 0 0" }}><span className="lbl">Text</span></label>
                    <AiTextArea value={sp.text} onChange={(v) => updSP(i, { text: v })} label={`Clause amendment — ${sp.heading || "provision"}${sp.clauseRef ? ` (Sub-Clause ${sp.clauseRef})` : ""}`} context={{ clauseRef: sp.clauseRef, kind: sp.kind, heading: sp.heading }} />
                    <button className="link" onClick={() => rmSP(i)} style={{ marginTop: 6 }}>Remove</button>
                  </div>
                ))}
                <button onClick={addSP}>+ Add amendment</button>
              </div>

              <div className="group">
                <div className="glabel">Special Conditions of Contract</div>
                <div className="hint">
                  {brief.specialConditionsBlocks?.length
                    ? `Included as a standalone section (${brief.specialConditionsTitle || "Special Conditions"}${brief.specialConditionsDate ? `, ${brief.specialConditionsDate}` : ""}) — 12-clause document.`
                    : "Not included."}
                </div>
                <div className="row">
                  <label><span className="lbl">Section title</span><input value={brief.specialConditionsTitle || ""} onChange={(e) => patch({ specialConditionsTitle: e.target.value })} /></label>
                  <label><span className="lbl">Dated</span><input value={brief.specialConditionsDate || ""} onChange={(e) => patch({ specialConditionsDate: e.target.value })} /></label>
                </div>
                {brief.specialConditionsBlocks?.length ? (
                  <button className="link" onClick={() => patch({ specialConditionsBlocks: [] })}>Remove Special Conditions section</button>
                ) : (
                  <button className="link" onClick={() => patch({ specialConditionsBlocks: SAMPLE_PROJECT.specialConditionsBlocks })}>Restore standard Special Conditions</button>
                )}
              </div>

              <div className="group">
                <div className="glabel">Drawing register ({brief.drawings.length})</div>
                <div className="dwg-row dwg-head"><div>No.</div><div>Title</div><div>Rev</div><div>Discipline</div><div /></div>
                {brief.drawings.map((d, i) => (
                  <div className="dwg-row" key={i}>
                    <input value={d.number} onChange={(e) => updDrawing(i, { number: e.target.value })} placeholder="A-101" />
                    <input value={d.title} onChange={(e) => updDrawing(i, { title: e.target.value })} placeholder="Title" />
                    <input value={d.revision || ""} onChange={(e) => updDrawing(i, { revision: e.target.value })} placeholder="1" />
                    <input value={d.discipline || ""} onChange={(e) => updDrawing(i, { discipline: e.target.value })} placeholder="Arch." />
                    <button className="iconbtn" onClick={() => rmDrawing(i)}>✕</button>
                  </div>
                ))}
                <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                  <button onClick={addDrawing}>+ Add drawing</button>
                  <button onClick={() => setShowPaste((s) => !s)}>{showPaste ? "− Paste list" : "⎘ Paste sheet list"}</button>
                  <button onClick={() => setShowAnalysis((s) => !s)} disabled={!brief.drawings.length}>{showAnalysis ? "− Analysis" : "⚲ Analyze"}</button>
                </div>
                {showAnalysis && <DrawingAnalysisPanel drawings={brief.drawings} />}
                {showPaste && (
                  <div className="card" style={{ marginTop: 8 }}>
                    <div className="hint">Paste a whole drawing index or set — it auto-detects the sheet-list lines and ignores titles, headers, notes and page numbers. Free-form ("A-101 Ground Floor Plan Rev 2") or tab/CSV columns. Discipline is inferred from the sheet code.</div>
                    <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={6} placeholder={"A-101  Ground Floor Plan  Rev 2\nS-200  Foundation Plan\nEL-301 Lighting Layout Rev 3"} />
                    {(() => { const parsed = parseDrawingList(pasteText); return (
                      <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 8 }}>
                        <button className="dark" disabled={!parsed.length} onClick={() => { setBrief((b) => ({ ...b, drawings: [...b.drawings, ...parsed] })); setPasteText(""); setShowPaste(false); }}>Append {parsed.length || ""} sheet(s)</button>
                        <button disabled={!parsed.length} onClick={() => { setBrief((b) => ({ ...b, drawings: parsed })); setPasteText(""); setShowPaste(false); }}>Replace register</button>
                        {pasteText.trim() && <span className="hint" style={{ margin: 0 }}>{parsed.length} parsed</span>}
                      </div>
                    ); })()}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              {/* Where this certificate is recorded. Every certificate lives in the
                  payments ledger under a contract; this picks which one, and the
                  ledger's own figures (next number, previously certified, advance
                  paid) feed the build-up so the two never drift apart. */}
              <div className="group ipc-target">
                <div className="glabel">Record under</div>
                {ledgerProjects.length === 0 ? (
                  <div className="hint" style={{ margin: 0 }}>No ledger yet — issuing this certificate creates a project for “{brief.projectName || "this contract"}” with a prime contract for {brief.contractor.name || "the Contractor"}.</div>
                ) : (
                  <>
                    <div className="row">
                      <label><span className="lbl">Ledger project</span>
                        <select value={ipcTarget?.projectId || ""} onChange={(e) => { const p = ledgerProjects.find((x) => x.id === e.target.value); const c = p?.contracts.find((x) => x.kind === "prime") || p?.contracts[0]; if (p && c) setTarget({ projectId: p.id, contractId: c.id }); }}>
                          <option value="">— choose —</option>
                          {ledgerProjects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.reference ? ` (${p.reference})` : ""}</option>)}
                        </select>
                      </label>
                      <label><span className="lbl">Contract</span>
                        <select value={ipcTarget?.contractId || ""} disabled={!ipcTarget} onChange={(e) => { if (ipcTarget) setTarget({ projectId: ipcTarget.projectId, contractId: e.target.value }); }}>
                          {(ledgerProjects.find((p) => p.id === ipcTarget?.projectId)?.contracts || []).map((c) => <option key={c.id} value={c.id}>{c.name || "(unnamed)"} — {c.party}</option>)}
                        </select>
                      </label>
                    </div>
                    {targetContract ? (
                      <div className="ipc-target-pos">
                        <span>Certified <b>{tfmt(worksCertified(targetContract))}</b>{targetContract.value != null && isFinite(targetContract.value) ? ` of ${tfmt(targetContract.value)}` : ""}</span>
                        <span>Advance paid <b>{tfmt(advancePaidOn(targetContract))}</b></span>
                        <span>{targetContract.certificates.length} certificate{targetContract.certificates.length === 1 ? "" : "s"}</span>
                        <button className="link" onClick={fillFromLedger} title="Set the certificate number, currency, retention and previously-certified figure from this contract's ledger">↓ Fill from ledger</button>
                      </div>
                    ) : (
                      <div className="hint" style={{ margin: "4px 0 0" }}>Choose the ledger contract this certificate belongs to.</div>
                    )}
                  </>
                )}
              </div>
              <div className="group">
                <div className="glabel">Certificate</div>
                <div className="row-3">
                  <label><span className="lbl">Type</span>
                    <select value={ipc.kind} onChange={(e) => { const k = e.target.value as IpcData["kind"]; patchIpc({ kind: k, certificateNo: targetContract ? nextCertNumber(targetContract, k === "advance" ? "advance" : k === "final" ? "final" : "interim") : k === "advance" ? "IPC.A.1" : ipc.certificateNo }); }}>
                      <option value="interim">Interim (IPC)</option><option value="advance">Advance</option><option value="final">Final</option>
                    </select>
                  </label>
                  <label><span className="lbl">Certificate no.</span><input value={ipc.certificateNo} onChange={(e) => patchIpc({ certificateNo: e.target.value })} /></label>
                  <label><span className="lbl">Date</span><input value={ipc.date} onChange={(e) => patchIpc({ date: e.target.value })} placeholder="27.04.2025" /></label>
                </div>
                <div className="row-3">
                  <label><span className="lbl">Currency</span><input list="ccy-list-ipc" value={ipc.currency} onChange={(e) => patchIpc({ currency: e.target.value.toUpperCase() })} placeholder={brief.contractPrice.currency} /><datalist id="ccy-list-ipc">{["GYD", "USD", "EUR", "GBP", "CAD", "TTD", "BBD", "XCD", "SRD", "BRL", "CNY", "INR", "AED"].map((x) => <option key={x} value={x} />)}</datalist></label>
                  <label><span className="lbl">Certifying Engineer</span><input value={ipc.engineerName || ""} onChange={(e) => patchIpc({ engineerName: e.target.value })} placeholder={brief.engineer?.name} /></label>
                  <label><span className="lbl">Payment due (days · 14.7)</span><input type="number" value={numStr(ipc.paymentDueDays)} onChange={(e) => patchIpc({ paymentDueDays: num(e.target.value) })} /></label>
                </div>
                <div className="row-3">
                  <label><span className="lbl">Statement ref (14.3)</span><input value={ipc.statementRef || ""} onChange={(e) => patchIpc({ statementRef: e.target.value })} /></label>
                  <label><span className="lbl">Statement date</span><input value={ipc.statementDate || ""} onChange={(e) => patchIpc({ statementDate: e.target.value })} /></label>
                  <div />
                </div>
                <div className="row">
                  <label><span className="lbl">Period from</span><input value={ipc.periodFrom || ""} onChange={(e) => patchIpc({ periodFrom: e.target.value })} /></label>
                  <label><span className="lbl">Period to</span><input value={ipc.periodTo || ""} onChange={(e) => patchIpc({ periodTo: e.target.value })} /></label>
                </div>
              </div>

              <div className="group">
                <div className="glabel">Prepare from a document <span className="ai-badge ok" style={{ fontWeight: 500 }}>smart</span></div>
                <div className="hint">Upload a PDF — an interim works statement, an advance-payment request, or a further installment / release notice. AI reads the amounts, sets the certificate type, and fills the build-up for you to check. (Supplier invoices → use Payments → Scan quotation.)</div>
                <button onClick={() => ipcScanRef.current?.click()} disabled={ipcScanBusy}>
                  {ipcScanBusy ? <span className="spinner dark-spin" /> : "⤒ Scan invoice / statement"}
                </button>
                <input ref={ipcScanRef} type="file" accept="application/pdf" style={{ display: "none" }} onChange={(e) => scanIpcInvoice(e.target.files?.[0])} />
                {ipcScanMsg && <div className="hint" style={{ color: /failed/i.test(ipcScanMsg) ? "#a3402f" : "#2f7d4f", marginTop: 6 }}>{ipcScanMsg}</div>}
              </div>

              {ipc.kind === "advance" ? (
                <div className="group">
                  <div className="glabel">Advance Payment (Sub-Clause 14.2) — no Works value</div>
                  <div className="hint">Special certificate for the advance / mobilisation payment against the Advance Payment Guarantee. No valuation of the Works is included (Works value = Nil).</div>
                  <div className="row-3">
                    <label><span className="lbl">Advance %</span><input type="number" value={numStr(ipc.advancePercent)} onChange={(e) => patchIpc({ advancePercent: num(e.target.value) })} /></label>
                    <label><span className="lbl">Installment #</span><input type="number" value={numStr(ipc.installmentIndex)} onChange={(e) => patchIpc({ installmentIndex: num(e.target.value) })} /></label>
                    <label><span className="lbl">Of</span><input type="number" value={numStr(ipc.installmentCount)} onChange={(e) => patchIpc({ installmentCount: num(e.target.value) })} /></label>
                  </div>
                  <div className="row" style={{ gridTemplateColumns: "1fr auto", alignItems: "end" }}>
                    <label style={{ margin: 0 }}><span className="lbl">Amount disbursed this certificate</span>
                      <input type="number" value={numStr(ipc.advanceAmount)} onChange={(e) => patchIpc({ advanceAmount: num(e.target.value) })} placeholder="e.g. 700000" />
                    </label>
                    {brief.advanceFixedStart?.amount ? (
                      <button className="link" onClick={() => patchIpc({ currency: brief.advanceFixedStart!.currency, advanceAmount: brief.advanceFixedStart!.amount, installmentCount: ipc.installmentCount || brief.advanceInstallments })}>
                        Use project fixed start ({brief.advanceFixedStart.currency} {brief.advanceFixedStart.amount.toLocaleString()})
                      </button>
                    ) : null}
                  </div>
                  <div className="row">
                    <label><span className="lbl">Advance Payment Guarantee ref (14.2.1)</span><input value={ipc.guaranteeRef || ""} onChange={(e) => patchIpc({ guaranteeRef: e.target.value })} /></label>
                    <label><span className="lbl">Amount in words <span className="ai-badge ok" style={{ fontWeight: 500 }}>auto from amount</span></span><input readOnly value={amountToWords({ amount: ipc.advanceAmount ?? NaN, currency: ipc.currency }) || "—"} style={{ background: "#f4f3ef", color: "var(--muted)" }} title="Always derived from the amount disbursed and currency, so it can never disagree with the figure" /></label>
                  </div>
                </div>
              ) : (
                <>
                  <div className="group">
                    <div className="glabel">A · Valuation of the Works to date (14.3), cumulative</div>
                    <div className="row-3">
                      <label><span className="lbl">Works executed (14.3a)</span><input type="number" value={numStr(ipc.valuation?.worksExecuted)} onChange={(e) => patchVal({ worksExecuted: num(e.target.value) })} /></label>
                      <label><span className="lbl">Plant & Materials (14.5)</span><input type="number" value={numStr(ipc.valuation?.materialsOnSite)} onChange={(e) => patchVal({ materialsOnSite: num(e.target.value) })} /></label>
                      <label><span className="lbl">Variations (13.3)</span><input type="number" value={numStr(ipc.valuation?.variations)} onChange={(e) => patchVal({ variations: num(e.target.value) })} /></label>
                    </div>
                    <div className="row-3">
                      <label><span className="lbl">Provisional Sums (13.4)</span><input type="number" value={numStr(ipc.valuation?.provisionalSums)} onChange={(e) => patchVal({ provisionalSums: num(e.target.value) })} /></label>
                      <label><span className="lbl">Dayworks (13.5)</span><input type="number" value={numStr(ipc.valuation?.dayworks)} onChange={(e) => patchVal({ dayworks: num(e.target.value) })} /></label>
                      <label><span className="lbl">Adjustments (13.6/13.7)</span><input type="number" value={numStr(ipc.valuation?.priceAdjustment)} onChange={(e) => patchVal({ priceAdjustment: num(e.target.value) })} /></label>
                    </div>
                    <label><span className="lbl">Other additions (claims, etc.)</span><input type="number" value={numStr(ipc.valuation?.otherAdditions)} onChange={(e) => patchVal({ otherAdditions: num(e.target.value) })} /></label>
                  </div>

                  <div className="group">
                    <div className="glabel">B · Retention, advance & previous (14.3 / 14.2.3)</div>
                    <div className="row-3">
                      <label><span className="lbl">Retention %</span><input type="number" value={numStr(ipc.retentionPercent)} onChange={(e) => patchIpc({ retentionPercent: num(e.target.value) })} /></label>
                      <label><span className="lbl">Retention limit %</span><input type="number" value={numStr(ipc.retentionLimitPercent)} onChange={(e) => patchIpc({ retentionLimitPercent: num(e.target.value) })} /></label>
                      <label><span className="lbl">Retention released</span><input type="number" value={numStr(ipc.retentionReleased)} onChange={(e) => patchIpc({ retentionReleased: num(e.target.value) })} /></label>
                    </div>
                    <div className="row-3">
                      <label><span className="lbl">Advance recovered to date</span><input type="number" value={numStr(ipc.advanceRecoveredToDate)} onChange={(e) => patchIpc({ advanceRecoveredToDate: num(e.target.value) })} /></label>
                      <label><span className="lbl">Previously certified</span><input type="number" value={numStr(ipc.previouslyCertified)} onChange={(e) => patchIpc({ previouslyCertified: num(e.target.value) })} /></label>
                      <label><span className="lbl">VAT %</span><input type="number" value={numStr(ipc.vatPercent)} onChange={(e) => patchIpc({ vatPercent: num(e.target.value) })} /></label>
                    </div>
                    <label><span className="lbl">Amount in words <span className="ai-badge ok" style={{ fontWeight: 500 }}>auto from amount</span></span><input readOnly value={amountToWords({ amount: computeIpc(brief, ipc).totalPayable, currency: ipc.currency }) || "—"} style={{ background: "#f4f3ef", color: "var(--muted)" }} title="Always derived from the amount due this Certificate and currency, so it can never disagree with the figure" /></label>
                  </div>

                  <div className="ipc-summary">
                    {(() => { const s = computeIpc(brief, ipc); const f = (x: number) => `${ipc.currency} ${x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; return (
                      <>
                        <div className="ipc-row"><span>Gross value of work done to date</span><b>{f(s.gross)}</b></div>
                        <div className="ipc-row"><span>Less retention held</span><span>({f(s.retentionHeld)})</span></div>
                        <div className="ipc-row"><span>Less advance recovered</span><span>({f(s.advanceRecovered)})</span></div>
                        <div className="ipc-row"><span>Less previously certified</span><span>({f(s.previouslyCertified)})</span></div>
                        {ipc.vatPercent ? <div className="ipc-row"><span>Add VAT @ {ipc.vatPercent}%</span><span>{f(s.vat)}</span></div> : null}
                        <div className="ipc-row total"><span>Amount due this Certificate</span><b>{f(s.totalPayable)}</b></div>
                        {s.belowMinimum ? <div className="hint" style={{ color: "#a3402f" }}>Below the minimum IPC amount in the Contract Data (14.6.2).</div> : null}
                      </>
                    ); })()}
                  </div>
                </>
              )}
            </>
          )}

          {mode !== "review" && (
            <div className="generate">
              {mode === "contract" && (
                <div className="gen-bar">
                  <button className="gen-act" onClick={doSave} title="Save this contract to your projects (syncs to your account when signed in)">💾 Save</button>
                  <button className="gen-act" onClick={doSaveAs} title="Save as a new or renamed project">Save as…</button>
                  <span className="gen-sep" />
                  <button className={`gen-tab ${panel === "share" ? "on" : ""}`} onClick={() => (panel === "share" ? setPanel(null) : doShareContract())} disabled={shareBusy} title="Share a live link to this contract (recipients can view and comment)">🔗 Share</button>
                  {!design && (
                    <button className={`gen-tab ${panel === "letters" ? "on" : ""}`} onClick={() => setPanel(panel === "letters" ? null : "letters")} title="Issue the contract-administration letters — site handover, notice to proceed, request for programme">
                      ✉ Letters
                    </button>
                  )}
                  <button className={`gen-tab ${panel === "executed" ? "on" : ""}`} onClick={() => setPanel(panel === "executed" ? null : "executed")} title="Attach the executed (signed) contract">
                    📎 Executed{brief.executedContract && <i className="gen-dot" title="Signed original on file" />}
                  </button>
                  <button className={`gen-tab ${panel === "ledger" ? "on" : ""}`} onClick={() => setPanel(panel === "ledger" ? null : "ledger")} title="Contract position and advance payment certificates">
                    ⚡ Ledger
                  </button>
                  {savedTick && <span className="proj-tick gen-tick">{savedTick}</span>}
                </div>
              )}
              {/* Drawer — one panel at a time, so the sticky bar stays a thin strip
                  instead of covering the form behind it. Everything here used to be
                  stacked open at once. */}
              {mode === "contract" && panel === "letters" && (
                <div className="gen-panel">
                  <div className="gen-panel-head">
                    <span className="lbl" style={{ margin: 0 }}>Issue a letter</span>
                    <span className="muted" style={{ fontSize: 11.5 }}>Issued by the {brief.engineer?.name?.trim() ? "Consultant" : "Employer"} — no Contractor signature required</span>
                  </div>
                  <div className="letter-sign">
                    {approvalSig ? (
                      <label className="letter-sign-toggle" title="Apply your saved approval signature to the letter as issued">
                        <input type="checkbox" checked={signLetters} onChange={(e) => setSignLetters(e.target.checked)} />
                        <img src={approvalSig} alt="signature" className="letter-sign-img" />
                        <span>Sign digitally as {brief.engineer?.name?.trim() ? "Consultant" : "Employer"}</span>
                      </label>
                    ) : (
                      <span className="muted" style={{ fontSize: 11.5 }}>To sign digitally, set your approval signature in Payments → “✎ Set approval signature”.</span>
                    )}
                  </div>
                  {LETTERS.map((l) => (
                    <div className="letter-row" key={l.kind}>
                      <span className="letter-name">{l.name}<span className="letter-ref">{l.clause}</span></span>
                      <button onClick={() => genLetter(l.kind, "docx")} disabled={!!letterBusy} title={`Generate ${l.name} as a Word document and log it`}>
                        {letterBusy === l.kind + "docx" ? <span className="spinner dark-spin" /> : "⤓ Word"}
                      </button>
                      <button onClick={() => genLetter(l.kind, "pdf")} disabled={!!letterBusy} title={`Generate ${l.name} as a PDF and log it`}>
                        {letterBusy === l.kind + "pdf" ? <span className="spinner dark-spin" /> : "⤓ PDF"}
                      </button>
                      <button onClick={() => doShareLetter(l.kind)} disabled={!!letterBusy} title={`Create a live link to ${l.name} to send by WhatsApp or email`}>
                        {letterBusy === l.kind + "share" ? <span className="spinner dark-spin" /> : "🔗 Share"}
                      </button>
                    </div>
                  ))}
                  {letterShare && (
                    <div className="share-box" style={{ marginTop: 8 }}>
                      <div className="lbl">Live link — {subjectFor(letterShare.kind)}</div>
                      <div className="share-row">
                        <input readOnly value={letterShareUrl} onFocus={(e) => e.currentTarget.select()} />
                        <button onClick={copyLetterLink}>{letterCopied ? "Copied ✓" : "Copy"}</button>
                        <a className="btnlink" href={letterShareUrl} target="_blank" rel="noreferrer">Open</a>
                      </div>
                      <div className="share-row">
                        <ShareButtons
                          url={letterShareUrl}
                          emails={[brief.contractor?.email, brief.employer?.email, brief.engineer?.email]}
                          subject={`${letterRef(letterShare.kind)} — ${subjectFor(letterShare.kind)}`}
                          message={letterTransmittal(letterShare.kind)}
                        />
                      </div>
                      <div className="share-actions">
                        <button className="link" onClick={() => setLetterShare(null)}>Hide</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* The executed contract. What this app generates is the draft; once
                  the parties sign, the scanned original is what governs — so keep
                  it attached to the project rather than loose on someone's disk. */}
              {mode === "contract" && panel === "executed" && (
                <div className="gen-panel">
                  <div className="gen-panel-head">
                    <span className="lbl" style={{ margin: 0 }}>Executed contract</span>
                    {brief.executedContract ? (
                      <span className="executed-tick">✓ Signed original on file</span>
                    ) : (
                      <span className="muted" style={{ fontSize: 11.5 }}>Attach the signed original once the parties execute</span>
                    )}
                  </div>
                  <FileAttach
                    file={brief.executedContract}
                    label="Upload signed contract"
                    title="Upload the executed contract as signed by the parties (PDF or scan, max 15 MB)"
                    onChange={(f) => setBrief((b) => ({ ...b, executedContract: f }))}
                  />
                  {brief.executedContract && (
                    <label className="executed-date">
                      Date executed
                      <input value={brief.executedDate || ""} onChange={(e) => setBrief((b) => ({ ...b, executedDate: e.target.value }))} placeholder="e.g. 17 July 2026" />
                    </label>
                  )}
                </div>
              )}
              {mode === "contract" && panel === "ledger" && (
                <div className="gen-panel">
                  {(() => {
                    const pos = contractLedgerPosition();
                    const m = (n: number | null) => (n != null && isFinite(n) ? `${pos.cur} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");
                    return pos.hasLedger ? (
                      <div className="cpos" style={{ marginBottom: 8 }}>
                        <div className="cpos-head"><span>Contract position</span><button className="link" onClick={() => goToPayments(true)}>view ledger →</button></div>
                        <div className="cpos-main"><b>{m(pos.certified)}</b> certified{pos.pct != null && <span className="cpos-pct"> · {pos.pct.toFixed(1)}% of {m(pos.value)}</span>}</div>
                        {pos.pct != null && <div className="pos-bar"><span style={{ width: `${Math.min(100, Math.max(0, pos.pct))}%` }} className={pos.pct > 100 ? "over" : ""} /></div>}
                        <div className="cpos-sub">{pos.certCount} certificate(s) · retention held {m(pos.retentionHeld)} · remaining {m(pos.remaining)}</div>
                        {pos.advancePaid > 0 && <div className="cpos-sub">Advance paid (repayable, 14.2) {m(pos.advancePaid)} — recovered from IPCs, not counted against the contract value</div>}
                      </div>
                    ) : (
                      <div className="hint" style={{ margin: "0 0 8px" }}>No payments ledger linked to this contract yet — generating the advance certificate(s) creates one.</div>
                    );
                  })()}
                  <button onClick={generateAdvanceCerts} style={{ width: "100%" }} title="Read the contract's advance terms, create the linked payments ledger, and generate the advance payment certificate(s)">⚡ Generate advance certificate(s) &amp; ledger →</button>
                  {ledgerMsg && <div className="hint" style={{ color: "#2f7d4f", marginTop: 6 }}>{ledgerMsg}</div>}
                </div>
              )}
              {mode === "contract" && panel === "share" && (
                <div className="share-box" style={{ marginBottom: 8 }}>
                  {brief.reviewId ? (
                    <>
                      <div className="lbl">Live contract link — anyone with it can view {reviewList.length ? "and comment" : "the document"}</div>
                      <div className="share-row">
                        <input readOnly value={reviewUrl} onFocus={(e) => e.currentTarget.select()} />
                        <button onClick={copyLink}>{copied ? "Copied ✓" : "Copy"}</button>
                        <a className="btnlink" href={reviewUrl} target="_blank" rel="noreferrer">Open</a>
                      </div>
                      <div className="share-row">
                        <ShareButtons url={reviewUrl} title={brief.projectName} emails={[brief.employer?.email, brief.contractor?.email, brief.engineer?.email]} />
                      </div>
                      <div className="share-actions">
                        <button onClick={pushDocUpdate} disabled={shareBusy}>{shareBusy ? <span className="spinner dark-spin" /> : "↻ Update link with current version"}</button>
                        <button className="link" onClick={() => setPanel(null)}>Hide</button>
                      </div>
                    </>
                  ) : (
                    <div className="share-actions">
                      {shareBusy ? <span className="spinner" /> : <span className="hint" style={{ margin: 0 }}>Preparing your share link…</span>}
                      <button className="link" onClick={() => setPanel(null)}>Cancel</button>
                    </div>
                  )}
                  {shareErr && <div className="hint" style={{ color: "#a3402f" }}>{shareErr}</div>}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button className="dark" style={{ flex: 2 }} onClick={onGenerate} disabled={genBusy}>
                  {genBusy ? <span className="spinner" /> : "⤓ Word (.docx)"}
                </button>
                <button style={{ flex: 2 }} onClick={onExportPdf} disabled={pdfBusy}>
                  {pdfBusy ? <span className="spinner dark-spin" /> : "⤓ PDF"}
                </button>
                <select value={paperSize} onChange={(e) => setPaperSize(e.target.value as PaperSize)} title="Paper size" style={{ flex: 1, width: "auto" }}>
                  <option value="A4">A4</option>
                  <option value="LETTER">Letter</option>
                </select>
              </div>
              {pdfErr && <div className="hint" style={{ color: "#a3402f", marginTop: 6 }}>{pdfErr}</div>}
              {mode === "ipc" && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button className="dark" onClick={recordIpcInLedger} disabled={ledgerBusy} style={{ flex: 2 }} title={targetContract ? `Issue this certificate and record it in the ledger under “${targetContract.name}” (re-issuing the same number updates it)` : "Issue this certificate and record it in the ledger (a linked project and prime contract are created if none exist)"}>
                      {ledgerBusy ? <span className="spinner" /> : <>✓ Issue &amp; record under {targetContract ? targetContract.name : "the ledger"} →</>}
                    </button>
                    <button style={{ flex: 1 }} onClick={doShareCert} disabled={shareBusy} title="Share a live link to this payment certificate">🔗 Share</button>
                  </div>
                  {ledgerMsg && <div className="hint" style={{ color: "#2f7d4f", marginTop: 6 }}>{ledgerMsg}</div>}
                  {showCertShare && (
                    <div className="share-box" style={{ marginTop: 8 }}>
                      {certReviewId ? (
                        <>
                          <div className="lbl">Live certificate link — anyone with it can view this payment certificate</div>
                          <div className="share-row">
                            <input readOnly value={certReviewUrl} onFocus={(e) => e.currentTarget.select()} />
                            <button onClick={copyCertLink}>{certCopied ? "Copied ✓" : "Copy"}</button>
                            <a className="btnlink" href={certReviewUrl} target="_blank" rel="noreferrer">Open</a>
                          </div>
                          <div className="share-row">
                            <ShareButtons url={certReviewUrl} title={certTitle()} emails={[brief.employer?.email, brief.contractor?.email, brief.engineer?.email]} />
                          </div>
                          <div className="share-actions">
                            <button onClick={doShareCert} disabled={shareBusy}>{shareBusy ? <span className="spinner dark-spin" /> : "↻ Update link with current version"}</button>
                            <button className="link" onClick={() => setShowCertShare(false)}>Hide</button>
                          </div>
                        </>
                      ) : (
                        <div className="share-actions">
                          {shareBusy ? <span className="spinner" /> : <span className="hint" style={{ margin: 0 }}>Preparing your share link…</span>}
                          <button className="link" onClick={() => setShowCertShare(false)}>Cancel</button>
                        </div>
                      )}
                      {shareErr && <div className="hint" style={{ color: "#a3402f" }}>{shareErr}</div>}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </>)}
        </div>
      </div>

      {mode !== "payments" && mode !== "scan" && (
        <div className="pane preview">
          <ContractPreview contract={contract} />
        </div>
      )}
    </div>
  );
}
